/**
 * Plan Manager plan configuration flow pure logic (Plans list, Setup, Notification, plans retrieval).
 */

// --- from planListUtils.js ---

/**
 * Placeholder shown when a plan field is missing or blank.
 * @constant {string}
 */
export const EMPTY_PLAN_FIELD_PLACEHOLDER = "--";

/**
 * Tone tokens describing how a plan status should be visually emphasized.
 * Components map these tokens to their own CSS classes; the util stays free of styling.
 * @readonly
 * @enum {string}
 */
export const PlanStatusTone = Object.freeze({
    COMPLETED: "completed",
    STARTED: "started",
    NOT_STARTED: "not-started",
});

export const PLAN_STATUS_DISPLAY_LABEL = Object.freeze({
    NOT_STARTED: "Not Started",
    STARTED: "Started",
    COMPLETED: "Completed",
});

/**
 * Whether a plan row can be started (Start icon shown, direct import allowed).
 * @param {string} status
 * @returns {boolean}
 */
export const canStartPlan = (status) =>
    status === PLAN_STATUS_DISPLAY_LABEL.NOT_STARTED;

/**
 * Whether a plan row can be edited (Edit icon shown).
 * @param {string} status
 * @returns {boolean}
 */
export const canEditPlan = (status) =>
    status === PLAN_STATUS_DISPLAY_LABEL.COMPLETED;

/**
 * Normalizes an arbitrary value to a lowercase, trimmed string for case-insensitive matching.
 * @param {unknown} value
 * @returns {string}
 */
const toComparableText = (value) =>
    value == null ? "" : String(value).toLowerCase().trim();

/**
 * Normalizes a plan id to the value the Plans list actually displays, so search
 * matches what the operator can see. HealthEdge rows drop the first four characters
 * (the `HRP_` prefix) exactly as {@link formatHealthEdgePlanId} does; ids of four or
 * fewer characters become empty so nothing hidden can match.
 * @param {unknown} planId
 * @param {boolean} shouldSliceHealthEdgePlanId
 * @returns {string}
 */
const toComparablePlanId = (planId, shouldSliceHealthEdgePlanId) => {
    const normalizedPlanId = toComparableText(planId);
    if (!shouldSliceHealthEdgePlanId) {
        return normalizedPlanId;
    }

    return normalizedPlanId.length > 4 ? normalizedPlanId.slice(4) : "";
};

/**
 * Filters plan rows by a free-text search against client name, client id, plan name,
 * and the plan id as displayed in the list. Matching is case-insensitive and
 * substring-based. An empty or whitespace-only criteria returns the original list
 * unchanged. For HealthEdge rows the displayed plan id has its `HRP_` prefix removed,
 * so passing `shouldSliceHealthEdgePlanId` keeps search aligned with the visible cell
 * instead of matching the hidden raw id.
 *
 * @param {Array<Object>} plans - In-memory plan rows to filter.
 * @param {string} searchCriteria - Raw user-typed search text.
 * @param {boolean} [shouldSliceHealthEdgePlanId] - When true, match the HealthEdge-formatted (prefix-stripped) plan id.
 * @returns {Array<Object>} The subset of `plans` matching the criteria.
 */
export const filterPlansBySearchCriteria = (
    plans,
    searchCriteria,
    shouldSliceHealthEdgePlanId = false,
) => {
    if (!Array.isArray(plans)) {
        return [];
    }

    const normalizedCriteria = toComparableText(searchCriteria);
    if (normalizedCriteria === "") {
        return plans;
    }

    const matchingPlans = plans.filter((plan) => {
        const clientName = toComparableText(plan?.clientName);
        const clientId = toComparableText(plan?.clientId);
        const planName = toComparableText(plan?.planName);
        const planId = toComparablePlanId(
            plan?.planId,
            shouldSliceHealthEdgePlanId,
        );

        return (
            clientName.includes(normalizedCriteria) ||
            clientId.includes(normalizedCriteria) ||
            planName.includes(normalizedCriteria) ||
            planId.includes(normalizedCriteria)
        );
    });

    return matchingPlans.sort((left, right) => {
        const byClientName = String(left.clientName).localeCompare(
            String(right.clientName),
        );
        if (byClientName !== 0) {
            return byClientName;
        }
        return String(left.planId).localeCompare(String(right.planId));
    });
};

/**
 * Formats a plain cell value, substituting a placeholder for missing/blank values.
 * @param {unknown} value
 * @returns {string}
 */
export const formatPlanCellValue = (value) => {
    if (value == null) {
        return EMPTY_PLAN_FIELD_PLACEHOLDER;
    }

    const text = String(value).trim();
    return text === "" ? EMPTY_PLAN_FIELD_PLACEHOLDER : text;
};

/**
 * Formats an ISO/date-like value to a locale date string, substituting a placeholder
 * for missing or unparseable values.
 * @param {unknown} value
 * @returns {string}
 */
export const formatPlanDate = (value) => {
    if (value == null || value === "") {
        return EMPTY_PLAN_FIELD_PLACEHOLDER;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return EMPTY_PLAN_FIELD_PLACEHOLDER;
    }

    return parsedDate.toLocaleDateString();
};

/**
 * Maps a plan status label to a visual tone token.
 * @param {unknown} status - Plan status label (e.g. "Completed", "Started").
 * @returns {string} A {@link PlanStatusTone} value.
 */
export const getPlanStatusTone = (status) => {
    switch (toComparableText(status)) {
        case "completed":
            return PlanStatusTone.COMPLETED;
        case "started":
            return PlanStatusTone.STARTED;
        default:
            return PlanStatusTone.NOT_STARTED;
    }
};

/**
 * Whether a chosen import file name contains the selected row's plan ID.
 * Matching is a case-insensitive substring check against the file name only —
 * the file contents are not read.
 *
 * @param {unknown} fileName - The browser-supplied file name (e.g. `Acme_PLAN-1001.json`).
 * @param {unknown} planId - The selected row's plan ID.
 * @returns {boolean} True when both values are non-empty and the file name includes the plan ID.
 */
export const doesFileNameIncludePlanId = (fileName, planId) => {
    const normalizedFileName = toComparableText(fileName);
    const normalizedPlanId = toComparableText(planId);

    if (normalizedFileName === "" || normalizedPlanId === "") {
        return false;
    }

    return normalizedFileName.includes(normalizedPlanId);
};

/**
 * Displays a HealthEdge plan ID the way the Plans list does: drop the first four characters.
 * Empty or shorter-than-four values use the empty-field placeholder.
 *
 * @param {unknown} planId
 * @returns {string}
 */
export const formatHealthEdgePlanId = (planId) => {
    const formattedPlanId = formatPlanCellValue(planId);
    if (formattedPlanId === EMPTY_PLAN_FIELD_PLACEHOLDER) {
        return EMPTY_PLAN_FIELD_PLACEHOLDER;
    }

    if (formattedPlanId.length <= 4) {
        return EMPTY_PLAN_FIELD_PLACEHOLDER;
    }

    return formattedPlanId.slice(4);
};

// --- from plansRetrievalUtils.js ---

export const PLAN_BATCH_CHUNK_SIZE = 80;

export const PLAN_STATUS_LABEL = Object.freeze({
    NOT_STARTED: "Not Started",
    STARTED: "Started",
    COMPLETED: "Completed",
});

/**
 * @param {{ completed?: boolean, started?: boolean }} planFlags
 * @returns {string}
 */
export const derivePlanStatusLabel = ({ completed, started }) => {
    if (completed === true) {
        return PLAN_STATUS_LABEL.COMPLETED;
    }
    if (started === true) {
        return PLAN_STATUS_LABEL.STARTED;
    }
    return PLAN_STATUS_LABEL.NOT_STARTED;
};

/**
 * @param {object} benefitPlan
 * @returns {object}
 */
export const mapBenefitPlanToGridRow = (benefitPlan) => {
    const clientId = benefitPlan?.clientId ?? "";
    const planId = benefitPlan?.planId ?? "";

    return {
        id: `${clientId}#${planId}`,
        clientName: benefitPlan?.clientName ?? "",
        clientId,
        planId,
        planName: benefitPlan?.planName ?? "",
        phoneNumberMemberServices: benefitPlan?.phoneNumberMemberServices ?? "",
        supportHours: benefitPlan?.supportHours ?? "",
        portalUrl: benefitPlan?.portalUrl ?? "",
        planStartDate: benefitPlan?.planStartDate ?? null,
        planEndDate: benefitPlan?.planEndDate ?? null,
        status: derivePlanStatusLabel({
            completed: benefitPlan?.completed,
            started: benefitPlan?.started,
        }),
        hasNotificationConfig: Boolean(benefitPlan?.notificationConfig),
        notificationConfig: benefitPlan?.notificationConfig ?? null,
        networkAffiliation: benefitPlan?.networkAffiliation ?? null,
    };
};

/**
 * @param {Array<{ clientId: string, planId: string }>} planRefs
 * @returns {Array<{ clientId: string, planId: string }>}
 */
export const dedupePlanRefs = (planRefs) => {
    if (!Array.isArray(planRefs)) {
        return [];
    }

    const seen = new Set();
    const uniqueRefs = [];

    for (const ref of planRefs) {
        const clientId = ref?.clientId ?? "";
        const planId = ref?.planId ?? "";
        const key = `${clientId}#${planId}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        uniqueRefs.push({ clientId, planId });
    }

    return uniqueRefs;
};

/**
 * @param {Array<{ clientId: string, planId: string }>} planRefs
 * @param {number} [chunkSize]
 * @returns {Array<Array<{ clientId: string, planId: string }>>}
 */
export const chunkPlanRefs = (planRefs, chunkSize = PLAN_BATCH_CHUNK_SIZE) => {
    const refs = Array.isArray(planRefs) ? [...planRefs] : [];
    const chunks = [];

    while (refs.length > 0) {
        chunks.push(refs.splice(0, chunkSize));
    }

    return chunks;
};

/**
 * @param {object} coverage
 * @returns {boolean}
 */
const isActiveClientCoverage = (coverage) => {
    if (!coverage?.terminationDate) {
        return true;
    }

    const coverageTermDate = new Date(coverage.terminationDate);
    coverageTermDate.setFullYear(coverageTermDate.getFullYear() + 1);
    return coverageTermDate > new Date();
};

/**
 * @param {Array<object>} items
 * @returns {Array<{ clientId: string, planId: string }>}
 */
export const buildJavelinaPlanRefsFromConfigItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    const planRefs = [];

    for (const item of items) {
        const clientCoverages = item?.clientCoverage ?? [];
        const clientIdFromKey = item?.["productType#planId#configType"]
            ?.split("#")?.[1];

        for (const coverage of clientCoverages) {
            if (!isActiveClientCoverage(coverage)) {
                continue;
            }

            const planIds = coverage?.clientPrimaryMedicalCoverage?.plans ?? [];
            for (const planId of planIds) {
                if (clientIdFromKey && planId) {
                    planRefs.push({ clientId: clientIdFromKey, planId });
                }
            }
        }
    }

    return planRefs;
};

/**
 * @param {Array<object>} items
 * @returns {Array<{ clientId: string, planId: string }>}
 */
export const buildHealthEdgePlanRefsFromConfigItems = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    const planRefs = [];

    for (const item of items) {
        const clientCoverages = item?.clientCoverage ?? [];
        const clientId = item?.clientId ?? "";

        for (const coverage of clientCoverages) {
            if (!isActiveClientCoverage(coverage)) {
                continue;
            }

            const planId = coverage?.plans;
            if (clientId && planId) {
                planRefs.push({ clientId, planId });
            }
        }
    }

    const serializedUniqueRefs = Array.from(
        new Set(planRefs.map((ref) => JSON.stringify(ref))),
    );

    return serializedUniqueRefs.map((ref) => JSON.parse(ref));
};

/**
 * @param {(lastEvaluatedKey?: object | string) => Promise<object | null>} fetchConfigPage
 * @returns {Promise<Array<object>>}
 */
export const paginateConfigScan = async (fetchConfigPage) => {
    const items = [];
    let lastEvaluatedKey;

    do {
        const response = await fetchConfigPage(lastEvaluatedKey);
        if (!response) {
            break;
        }

        const pageItems = response.Items ?? [];
        items.push(...pageItems);
        lastEvaluatedKey = response.LastEvaluatedKey;
    } while (lastEvaluatedKey !== undefined);

    return items;
};

/**
 * @param {object} params
 * @param {(lastEvaluatedKey?: object | string) => Promise<object | null>} params.fetchConfigPage
 * @param {(planRefs: Array<{ clientId: string, planId: string }>) => Promise<object | null>} params.fetchBatch
 * @param {(items: Array<object>) => Array<{ clientId: string, planId: string }>} params.buildRefsFromItems
 * @returns {Promise<Array<object>>}
 */
export const loadPlansGrid = async ({
    fetchConfigPage,
    fetchBatch,
    buildRefsFromItems,
}) => {
    const configItems = await paginateConfigScan(fetchConfigPage);
    const planRefs = dedupePlanRefs(buildRefsFromItems(configItems));
    const refChunks = chunkPlanRefs(planRefs);

    const batchResponses = await Promise.all(
        refChunks.map((chunk) => fetchBatch(chunk)),
    );

    const benefitPlans = batchResponses.flatMap(
        (response) => response?.Responses?.BenefitPlans ?? [],
    );

    return benefitPlans
        .map(mapBenefitPlanToGridRow)
        .filter((row) => Boolean(row.planId))
        .sort((left, right) => {
            const byClientName = String(left.clientName).localeCompare(
                String(right.clientName),
            );
            if (byClientName !== 0) {
                return byClientName;
            }
            return String(left.planId).localeCompare(String(right.planId));
        });
};

/**
 * @param {object} params
 * @param {(lastEvaluatedKey?: object | string) => Promise<object | null>} params.fetchJavelinaConfigPage
 * @param {(planRefs: Array<{ clientId: string, planId: string }>) => Promise<object | null>} params.fetchJavelinaBatch
 * @param {(lastEvaluatedKey?: object | string) => Promise<object | null>} params.fetchHealthEdgeConfigPage
 * @param {(planRefs: Array<{ clientId: string, planId: string }>) => Promise<object | null>} params.fetchHealthEdgeBatch
 * @returns {Promise<{ jvPlans: Array<object>, hrpPlans: Array<object> }>}
 */
export const loadAllPlansGrids = async ({
    fetchJavelinaConfigPage,
    fetchJavelinaBatch,
    fetchHealthEdgeConfigPage,
    fetchHealthEdgeBatch,
}) => {
    const [jvPlans, hrpPlans] = await Promise.all([
        loadPlansGrid({
            fetchConfigPage: fetchJavelinaConfigPage,
            fetchBatch: fetchJavelinaBatch,
            buildRefsFromItems: buildJavelinaPlanRefsFromConfigItems,
        }),
        loadPlansGrid({
            fetchConfigPage: fetchHealthEdgeConfigPage,
            fetchBatch: fetchHealthEdgeBatch,
            buildRefsFromItems: buildHealthEdgePlanRefsFromConfigItems,
        }),
    ]);

    return { jvPlans, hrpPlans };
};

// --- from planNotificationUtils.js ---

/**
 * Instructional helper text displayed above the auto-generated notifications table.
 * @constant {string}
 */
export const NOTIFICATIONS_TAB_HELPER_TEXT =
    "Below you'll find all the notifications that will be auto-generated based on the communications library you selected. No customization to the content in the communications is possible. If there are any communications that the client would like us to NOT send, toggle the communication to off. Please note that a fee will be assessed to the client for turning off automated communications - this needs to be communicated to Member Communications and the Client Team to ensure this fee gets charged.";

/**
 * Tab identifiers for the notification setup page.
 * @readonly
 * @enum {string}
 */
export const NotificationTabId = Object.freeze({
    VARIABLES: "variables",
    NOTIFICATIONS: "notifications",
});

/**
 * Returns the first option marked selected, or undefined.
 * @param {ReadonlyArray<{ isSelected?: boolean }> | undefined} options
 * @returns {object | undefined}
 */
export const getSelectedOption = (options) =>
    options?.find((option) => option.isSelected);

/**
 * Returns the selected communications library option.
 * @param {ReadonlyArray<object> | undefined} commsLibraryOptions
 * @returns {object | undefined}
 */
export const getSelectedCommsLibrary = (commsLibraryOptions) =>
    getSelectedOption(commsLibraryOptions);

/**
 * Whether the selected comms library is a partnership plan (or Cone).
 * @param {object | undefined} commsLibrary
 * @returns {boolean}
 */
export const isPartnershipPlan = (commsLibrary) =>
    Boolean(
        commsLibrary?.isPartnershipPlan || commsLibrary?.title === "Cone",
    );

/**
 * Whether RBP Yes is selected.
 * @param {ReadonlyArray<{ title?: string, isSelected?: boolean }> | undefined} rbpOptions
 * @returns {boolean}
 */
export const isRbpYes = (rbpOptions) =>
    getSelectedOption(rbpOptions)?.title === "Yes";

/**
 * Selected language title, if any.
 * @param {ReadonlyArray<{ title?: string, isSelected?: boolean }> | undefined} languages
 * @returns {string | undefined}
 */
export const getSelectedLanguage = (languages) =>
    getSelectedOption(languages)?.title;

/**
 * Text-code fields derived from the selected administrator.
 * @param {ReadonlyArray<object> | undefined} administratorName
 * @returns {{ textCode: string, textWord: string }}
 */
export const getAdministratorDerivedFields = (administratorName) => {
    const selected = getSelectedOption(administratorName);
    return {
        textCode: selected?.textCode != null ? String(selected.textCode) : "",
        textWord: selected?.textWord ?? "",
    };
};

/**
 * Network name options for the selected comms library.
 * @param {ReadonlyArray<object> | undefined} commsLibraryOptions
 * @returns {ReadonlyArray<object>}
 */
export const getNetworkNamesForSelectedLibrary = (commsLibraryOptions) =>
    getSelectedCommsLibrary(commsLibraryOptions)?.networkNames ?? [];

/**
 * Formats a 10-digit phone string as XXX-XXX-XXXX when possible.
 * @param {string | undefined} phoneNumber
 * @returns {string}
 */
export const formatMemberServicesPhone = (phoneNumber) => {
    if (!phoneNumber) {
        return "";
    }

    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const match = digitsOnly.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (!match) {
        return phoneNumber;
    }

    return `${match[1]}-${match[2]}-${match[3]}`;
};

/**
 * Display title for claim-details language options (legacy label tweak).
 * @param {string} title
 * @returns {string}
 */
export const getClaimDetailsLanguageDisplayTitle = (title) => {
    if (title === "Payer Compass and Asserta Health language") {
        return "Asserta Health language";
    }

    return title;
};

/**
 * Validates the Variables tab. An empty object means the form is valid.
 * @param {object} formState
 * @returns {Record<string, string | object>}
 */
export const validateVariablesForm = (formState) => {
    const validationErrors = {};

    if (!getSelectedOption(formState.languages)) {
        validationErrors.languages = "Language is required";
    }

    if (!formState.clientName?.trim()) {
        validationErrors.clientName = "Client name is required";
    }

    if (!getSelectedOption(formState.administratorName)) {
        validationErrors.administratorName = "Adminitstrator Name is required";
    }

    if (!getSelectedOption(formState.supportTeamName)) {
        validationErrors.supportTeamName = "Support Team Name is required";
    }

    const selectedCommsLib = getSelectedCommsLibrary(
        formState.commsLibraryOptions,
    );

    if (!selectedCommsLib) {
        validationErrors.commsLibraryOptions =
            "Communication Library Options is requried";
        return validationErrors;
    }

    if (isPartnershipPlan(selectedCommsLib)) {
        const selectedPcpCopay = getSelectedOption(formState.pcpCopay);

        if (!selectedPcpCopay) {
            validationErrors.pcpCopay = "PCP copay is required";
        } else if (
            selectedPcpCopay.title === "Other" &&
            !selectedPcpCopay.value?.trim()
        ) {
            validationErrors.pcpCopay = "PCP copay other requires an input";
        }

        const everyReferralHasSelection = (formState.referralOptions ?? []).every(
            ({ options }) =>
                options?.some((opt) => opt.isSelected && opt.value),
        );

        if (!everyReferralHasSelection) {
            validationErrors.referralOptions = "Referral option is requried";
        }
    } else {
        const selectedNetwork = getSelectedOption(
            selectedCommsLib.networkNames ?? [],
        );

        if (selectedCommsLib.networkNames && !selectedNetwork) {
            validationErrors.commsLibraryOptions = {
                networkNames: "Networkname is required",
            };
        } else if (
            selectedNetwork?.title === "Other" &&
            !selectedNetwork.value?.trim()
        ) {
            validationErrors.commsLibraryOptions = {
                networkNames:
                    "Networkname input is requried if others is selected",
            };
        }
    }

    if (!getSelectedOption(formState.rbpOptions)) {
        validationErrors.rbpOptions = "RBP option is requried";
    } else if (isRbpYes(formState.rbpOptions)) {
        const selectedClaimDetails = getSelectedOption(
            formState.claimDetailsLanguage,
        );

        if (
            selectedClaimDetails?.title !== "N/A" &&
            !selectedClaimDetails?.value?.trim()
        ) {
            validationErrors.claimDetailsLanguage =
                "Claim details language is required";
        }

        const selectedSelfReferral = getSelectedOption(formState.selfReferral);

        if (
            selectedSelfReferral?.title !== "N/A" &&
            !selectedSelfReferral?.value?.trim()
        ) {
            validationErrors.selfReferral = "SelfReferral is requried";
        }

        const selectedDependentReferral = getSelectedOption(
            formState.dependentReferral,
        );

        if (
            selectedDependentReferral?.title !== "N/A" &&
            !selectedDependentReferral?.value?.trim()
        ) {
            validationErrors.dependentReferral =
                "Dependent Referral is requried";
        }
    }

    return validationErrors;
};

/**
 * Applies network-specific template overrides to an event row (immutable).
 * @param {object} event
 * @param {object | undefined} selectedNetwork
 * @returns {object}
 */
const applyNetworkTemplateOverrides = (event, selectedNetwork) => {
    if (!event.templates || !selectedNetwork?.title) {
        return event;
    }

    const nextEvent = { ...event };
    const englishDefault = event.templates.find(
        (template) => template.language === "English",
    );
    const englishOverride = event.templates.find(
        (template) =>
            template.language === "English" &&
            template.networkFilters?.includes(selectedNetwork.title),
    );

    if (englishOverride) {
        nextEvent.defaultEn = englishOverride.title;
    } else if (englishDefault && nextEvent.defaultEn !== englishDefault.default) {
        nextEvent.defaultEn = englishDefault.default;
    }

    const spanishDefault = event.templates.find(
        (template) => template.language === "English + Spanish",
    );
    const spanishOverride = event.templates.find(
        (template) =>
            template.language === "English + Spanish" &&
            template.networkFilters?.includes(selectedNetwork.title),
    );

    if (spanishOverride) {
        nextEvent.defaultEnSp = spanishOverride.title;
    } else if (
        spanishDefault &&
        nextEvent.defaultEnSp !== spanishDefault.default
    ) {
        nextEvent.defaultEnSp = spanishDefault.default;
    }

    return nextEvent;
};

/**
 * Returns notification events for the selected comms library, with PMC filtering rules.
 * @param {ReadonlyArray<object> | undefined} commsLibraryOptions
 * @returns {ReadonlyArray<object>}
 */
export const getFilteredEvents = (commsLibraryOptions) => {
    const selectedLibrary = getSelectedCommsLibrary(commsLibraryOptions);

    if (!selectedLibrary?.events) {
        return [];
    }

    const selectedNetwork = getSelectedOption(selectedLibrary.networkNames ?? []);

    let events = selectedLibrary.events
        .filter((event) => event.title !== "cobReminder")
        .map((event) => applyNetworkTemplateOverrides(event, selectedNetwork));

    if (
        selectedLibrary.title === "TPA" &&
        selectedNetwork &&
        selectedNetwork.title !== "HPS"
    ) {
        events = events.filter((event) => event.title !== "newMemberHpsEoB");
    }

    return events;
};

/**
 * Whether any event is toggled off for the selected comms library.
 * @param {ReadonlyArray<object> | undefined} commsLibraryOptions
 * @returns {boolean}
 */
export const areAnyEventsOff = (commsLibraryOptions) =>
    getSelectedCommsLibrary(commsLibraryOptions)?.events?.some(
        (event) => !event.isSelected,
    ) ?? false;

/**
 * Fields excluded when comparing dirty notification config state.
 * @readonly
 * @type {ReadonlySet<string>}
 */
const DIRTY_COMPARE_EXCLUDED_KEYS = new Set([
    "selectedTabId",
    "showWarningModal",
    "planBasicInfo",
]);

/**
 * Whether the editable notification config differs from the saved snapshot.
 * @param {object | undefined} currentState
 * @param {object | undefined} originalState
 * @returns {boolean}
 */
export const hasNotificationConfigChanged = (currentState, originalState) => {
    if (!currentState || !originalState) {
        return false;
    }

    const comparableKeys = Object.keys(currentState).filter(
        (key) => !DIRTY_COMPARE_EXCLUDED_KEYS.has(key),
    );

    return comparableKeys.some((key) => {
        const originalValue = originalState[key];
        const currentValue = currentState[key];
        return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
    });
};

/**
 * Whether the Notifications tab may be selected (Variables form valid).
 * @param {object} formState
 * @returns {boolean}
 */
export const canSelectNotificationsTab = (formState) =>
    Object.keys(validateVariablesForm(formState)).length === 0;

/**
 * Whether Submit should be enabled.
 * @param {object} formState
 * @param {boolean} hasConfigChange
 * @returns {boolean}
 */
export const canSubmitNotificationConfig = (formState, hasConfigChange) =>
    canSelectNotificationsTab(formState) && hasConfigChange;

/**
 * Capture-primary key paths that a notification-config PUT body must cover.
 * @readonly
 */
export const REQUIRED_NOTIFICATION_UPDATE_KEY_PATHS = Object.freeze([
    "clientId",
    "planId",
    "notificationConfig",
    "notificationConfig.administratorName",
    "notificationConfig.administratorName.textCode",
    "notificationConfig.administratorName.textWord",
    "notificationConfig.administratorName.title",
    "notificationConfig.clientName",
    "notificationConfig.commsLibraryOptions",
    "notificationConfig.commsLibraryOptions.events",
    "notificationConfig.commsLibraryOptions.isPartnershipPlan",
    "notificationConfig.commsLibraryOptions.networkNames",
    "notificationConfig.commsLibraryOptions.subtitle",
    "notificationConfig.commsLibraryOptions.title",
    "notificationConfig.createdDate",
    "notificationConfig.languages",
    "notificationConfig.languages.title",
    "notificationConfig.rbpOptions",
    "notificationConfig.rbpOptions.title",
    "notificationConfig.referralOptions",
    "notificationConfig.supportTeamName",
    "notificationConfig.supportTeamName.title",
    "notificationConfig.updatedBy",
    "notificationConfig.updatedDate",
]);

/**
 * Capture-primary per-event keys required when an event object is present.
 * @readonly
 */
export const REQUIRED_NOTIFICATION_EVENT_KEY_PATHS = Object.freeze([
    "title",
    "method",
    "isSelected",
    "description",
    "type",
    "defaultEn",
    "defaultEnSp",
]);

/**
 * Deep-clones a value via JSON round-trip.
 * @param {*} value
 * @returns {*}
 */
const deepCloneJson = (value) => JSON.parse(JSON.stringify(value));

/**
 * Cleans a single-select option array into the selected wire object.
 * @param {ReadonlyArray<object>|undefined} options
 * @returns {object|undefined}
 */
export const findAndCleanData = (options) => {
    if (!Array.isArray(options)) {
        return undefined;
    }

    const cloned = deepCloneJson(options);
    const selected = cloned.find((option) => option?.isSelected === true);

    if (!selected) {
        return undefined;
    }

    delete selected.isSelected;
    delete selected.sortOrder;

    if (Array.isArray(selected.events)) {
        selected.events = selected.events.filter(
            (event) => !event.isSelected || event.isEOY,
        );
    }

    if (Array.isArray(selected.networkNames)) {
        selected.networkNames = selected.networkNames.filter(
            (network) => network.isSelected === true,
        );
    }

    return selected;
};

/**
 * Cleans referral option groups for the PUT body.
 * @param {ReadonlyArray<object>|undefined} referralOptions
 * @returns {Array<object>|undefined}
 */
export const cleanReferralOptions = (referralOptions) => {
    if (!Array.isArray(referralOptions)) {
        return undefined;
    }

    const cloned = deepCloneJson(referralOptions);

    return cloned
        .filter(
            (group) =>
                Array.isArray(group.options) &&
                group.options.some((option) => option.isSelected === true),
        )
        .map((group) => ({
            ...group,
            options: group.options.filter(
                (option) => option.isSelected === true,
            ),
        }));
};

/**
 * Forces `newMemberHpsEoB` off when TPA is selected with a non-HPS network.
 * @param {ReadonlyArray<object>|undefined} commsLibraryOptions
 * @returns {Array<object>|undefined}
 */
export const applyTpaNewMemberHpsEobGuard = (commsLibraryOptions) => {
    if (!Array.isArray(commsLibraryOptions)) {
        return commsLibraryOptions;
    }

    const cloned = deepCloneJson(commsLibraryOptions);
    const selectedLibrary = cloned.find(
        (option) => option?.isSelected === true,
    );

    if (!selectedLibrary || selectedLibrary.title !== "TPA") {
        return cloned;
    }

    const hasNonHpsSelectedNetwork = Array.isArray(selectedLibrary.networkNames)
        ? selectedLibrary.networkNames.some(
              (network) =>
                  network.isSelected === true && network.title !== "HPS",
          )
        : false;

    if (!hasNonHpsSelectedNetwork || !Array.isArray(selectedLibrary.events)) {
        return cloned;
    }

    selectedLibrary.events = selectedLibrary.events.map((event) => {
        if (event.title === "newMemberHpsEoB") {
            return { ...event, isSelected: false };
        }

        return event;
    });

    return cloned;
};

/**
 * Builds the notification-config PUT body from form state and loaded config.
 * @param {{
 *   form: object,
 *   loadedNotificationConfig?: object,
 *   clientId: string,
 *   planId: string,
 *   updatedBy: string,
 * }} params
 * @returns {{ clientId: string, planId: string, notificationConfig: object }}
 */
export const buildNotificationConfigUpdateBody = ({
    form = {},
    loadedNotificationConfig,
    clientId,
    planId,
    updatedBy,
}) => {
    const guardedCommsLibraryOptions = applyTpaNewMemberHpsEobGuard(
        form.commsLibraryOptions,
    );

    const notificationConfig = {
        ...(loadedNotificationConfig ?? {}),
    };

    notificationConfig.languages = findAndCleanData(form.languages);
    notificationConfig.commsLibraryOptions = findAndCleanData(
        guardedCommsLibraryOptions,
    );
    notificationConfig.administratorName = findAndCleanData(
        form.administratorName,
    );
    notificationConfig.supportTeamName = findAndCleanData(form.supportTeamName);
    notificationConfig.pcpCopay = findAndCleanData(form.pcpCopay);
    notificationConfig.rbpOptions = findAndCleanData(form.rbpOptions);
    notificationConfig.claimDetailsLanguage = findAndCleanData(
        form.claimDetailsLanguage,
    );
    notificationConfig.selfReferral = findAndCleanData(form.selfReferral);
    notificationConfig.dependentReferral = findAndCleanData(
        form.dependentReferral,
    );
    notificationConfig.clientName = form.clientName;
    notificationConfig.referralOptions = cleanReferralOptions(
        form.referralOptions,
    );

    if (notificationConfig.rbpOptions?.title === "No") {
        delete notificationConfig.claimDetailsLanguage;
    }

    Object.keys(notificationConfig).forEach((key) => {
        if (notificationConfig[key] === undefined) {
            delete notificationConfig[key];
        }
    });

    notificationConfig.updatedBy = updatedBy;

    return {
        clientId,
        planId,
        notificationConfig,
    };
};

/**
 * Deep-clones the default notification catalog for form hydration.
 * @param {Object} [defaultNotificationConfig] - Default catalog from getDefaultNotificationConfig.
 * @returns {Object} Cloned catalog object.
 */
export const cloneDefaultNotificationCatalog = (defaultNotificationConfig) => {
    if (
        !defaultNotificationConfig ||
        typeof defaultNotificationConfig !== "object"
    ) {
        return {};
    }

    return JSON.parse(JSON.stringify(defaultNotificationConfig));
};

/**
 * Merges a saved single-select field with catalog defaults.
 * @param {ReadonlyArray<object>} defaultValue - Catalog options for the field.
 * @param {object} selectedObject - Saved selected option.
 * @returns {Array<object>} Merged option list.
 */
const mergeSingleSelectNotificationOptions = (defaultValue, selectedObject) => {
    if (!Array.isArray(defaultValue) || !selectedObject) {
        return defaultValue;
    }

    return [
        ...defaultValue
            .filter((option) => option.title !== selectedObject.title)
            .map((option) => ({ ...option, isSelected: false })),
        {
            ...defaultValue.find((option) => option.title === selectedObject.title),
            ...selectedObject,
            isSelected: true,
        },
    ].sort((left, right) =>
        String(left.title).localeCompare(String(right.title)),
    );
};

/**
 * Merges saved referral option groups with catalog defaults.
 * @param {ReadonlyArray<object>} defaultValue - Catalog referral groups.
 * @param {ReadonlyArray<object>} selectedObject - Saved referral groups.
 * @returns {Array<object>} Merged referral groups.
 */
const mergeReferralNotificationOptions = (defaultValue, selectedObject) => {
    if (!Array.isArray(defaultValue) || !Array.isArray(selectedObject)) {
        return defaultValue;
    }

    const selectedTitles = selectedObject.map((group) => group.title).join(",");

    return [
        ...defaultValue.filter((group) => !selectedTitles.includes(group.title)),
        ...selectedObject.map((group) => {
            const defaultGroup = defaultValue.find(
                (item) => item.title === group.title,
            );
            const selectedOptionTitles = group.options
                .map((option) => option.label.title)
                .join(",");

            const options = [
                ...(defaultGroup?.options ?? [])
                    .filter(
                        (option) =>
                            !selectedOptionTitles.includes(option.label.title),
                    )
                    .map((option) => ({ ...option, isSelected: false })),
                ...group.options.map((option) => {
                    const matchedOption = defaultGroup?.options?.find(
                        (item) => item.label.title === option.label.title,
                    );

                    return {
                        ...option,
                        isSelected: true,
                        sortOrder: matchedOption?.sortOrder,
                    };
                }),
            ].sort((left, right) => left.sortOrder - right.sortOrder);

            return { ...group, options };
        }),
    ].sort((left, right) => left.title.localeCompare(right.title));
};

/**
 * Merges a saved comms library selection with catalog defaults.
 * @param {ReadonlyArray<object>} defaultValue - Catalog comms libraries.
 * @param {object} selectedObject - Saved comms library config.
 * @returns {Array<object>} Merged comms library list.
 */
const mergeCommsLibraryNotificationOptions = (defaultValue, selectedObject) => {
    if (!Array.isArray(defaultValue) || !selectedObject) {
        return defaultValue;
    }

    const savedEventsMap = new Map(
        (selectedObject.events ?? []).map((event) => [
            `${event.title}:${event.type}`,
            event,
        ]),
    );
    const selectedNetworkTitles = (selectedObject.networkNames ?? [])
        .map((network) => network.title)
        .join(",");

    return defaultValue.map((library) => {
        const nextLibrary = JSON.parse(JSON.stringify(library));

        if (selectedObject.title !== library.title) {
            nextLibrary.isSelected = false;
            return nextLibrary;
        }

        nextLibrary.isSelected = true;
        nextLibrary.events = nextLibrary.events.map((event) => {
            const eventKey = `${event.title}:${event.type}`;
            const nextEvent = { ...event };
            const savedEvent = savedEventsMap.get(eventKey);

            if (savedEvent) {
                if (typeof savedEvent.isSelected === "boolean") {
                    nextEvent.isSelected = savedEvent.isSelected;
                }

                if (savedEvent.isEOY) {
                    nextEvent.isEOY = true;
                    nextEvent.defaultEn = `${String(nextEvent.defaultEn).replace(/_EOY$/u, "")}_EOY`;
                    nextEvent.defaultEnSp = `${String(nextEvent.defaultEnSp).replace(/_EOY$/u, "")}_EOY`;
                }
            }

            return nextEvent;
        });

        if (selectedObject.networkNames && nextLibrary.networkNames) {
            nextLibrary.networkNames = nextLibrary.networkNames.map((network) => {
                if (selectedNetworkTitles.includes(network.title)) {
                    const selectedNetwork = selectedObject.networkNames.find(
                        (item) => item.title === network.title,
                    );

                    return { ...selectedNetwork, isSelected: true };
                }

                return { ...network, isSelected: false };
            });
        }

        return nextLibrary;
    });
};

/**
 * Merges one saved notification field with catalog defaults (legacy configureSelection).
 * @param {string} key - Notification form field key.
 * @param {*} selectedObject - Saved value for the field.
 * @param {Object} defaultCatalog - Cloned default notification catalog.
 * @returns {*} Merged field value.
 */
export const configureNotificationSelection = (
    key,
    selectedObject,
    defaultCatalog,
) => {
    if (!selectedObject) {
        return defaultCatalog[key];
    }

    const defaultValue = defaultCatalog[key];

    if (key === "referralOptions") {
        return mergeReferralNotificationOptions(defaultValue, selectedObject);
    }

    if (key === "commsLibraryOptions") {
        return mergeCommsLibraryNotificationOptions(defaultValue, selectedObject);
    }

    return mergeSingleSelectNotificationOptions(defaultValue, selectedObject);
};

/**
 * Builds editable notification form state from saved config and catalog defaults.
 * @param {Object} savedConfig - Saved notificationConfig from the plan row.
 * @param {Object} defaultCatalog - Cloned default notification catalog.
 * @returns {Object} Partial notification form state.
 */
export const mergeSavedNotificationConfigWithDefaults = (
    savedConfig,
    defaultCatalog,
) => ({
    languages: configureNotificationSelection(
        "languages",
        savedConfig.languages,
        defaultCatalog,
    ),
    commsLibraryOptions: configureNotificationSelection(
        "commsLibraryOptions",
        savedConfig.commsLibraryOptions,
        defaultCatalog,
    ),
    administratorName: configureNotificationSelection(
        "administratorName",
        savedConfig.administratorName,
        defaultCatalog,
    ),
    supportTeamName: configureNotificationSelection(
        "supportTeamName",
        savedConfig.supportTeamName,
        defaultCatalog,
    ),
    networkName: configureNotificationSelection(
        "networkName",
        savedConfig.networkName,
        defaultCatalog,
    ),
    pcpCopay: configureNotificationSelection(
        "pcpCopay",
        savedConfig.pcpCopay,
        defaultCatalog,
    ),
    referralOptions: configureNotificationSelection(
        "referralOptions",
        savedConfig.referralOptions,
        defaultCatalog,
    ),
    rbpOptions: configureNotificationSelection(
        "rbpOptions",
        savedConfig.rbpOptions,
        defaultCatalog,
    )?.sort((left, right) => right.title.localeCompare(left.title)),
    claimDetailsLanguage: configureNotificationSelection(
        "claimDetailsLanguage",
        savedConfig.claimDetailsLanguage,
        defaultCatalog,
    )?.sort((left, right) => left.sortOrder - right.sortOrder),
    selfReferral: configureNotificationSelection(
        "selfReferral",
        savedConfig.selfReferral,
        defaultCatalog,
    )?.sort((left, right) => left.sortOrder - right.sortOrder),
    dependentReferral: configureNotificationSelection(
        "dependentReferral",
        savedConfig.dependentReferral,
        defaultCatalog,
    )?.sort((left, right) => left.sortOrder - right.sortOrder),
});

/**
 * Builds first-time notification form state from the default catalog.
 * @param {Object} defaultCatalog - Default notification catalog.
 * @returns {Object} Notification form state.
 */
export const buildFirstTimeNotificationForm = (defaultCatalog) => ({
    ...cloneDefaultNotificationCatalog(defaultCatalog),
    clientName: "",
});

/**
 * Hydrates notification editor state from plan row context and default catalog.
 * @param {{ planRow?: object, defaultNotificationConfig?: object }} params
 * @returns {{ formData: Object, planBasicInfo: Object }}
 */
export const hydrateNotificationFormState = ({
    planRow,
    defaultNotificationConfig,
}) => {
    const planBasicInfo = {
        planName: planRow?.planName ?? "",
        phoneNumberMemberServices: planRow?.phoneNumberMemberServices ?? "",
        supportHours: planRow?.supportHours ?? "",
        portalUrl: planRow?.portalUrl ?? "",
        clientName: planRow?.clientName ?? "",
        clientId: planRow?.clientId ?? "",
        planId: planRow?.planId ?? "",
    };

    const defaultCatalog = cloneDefaultNotificationCatalog(
        defaultNotificationConfig,
    );

    if (!planRow) {
        return { formData: {}, planBasicInfo };
    }

    if (!planRow.notificationConfig) {
        return {
            formData: buildFirstTimeNotificationForm(defaultCatalog),
            planBasicInfo,
        };
    }

    const savedConfig = planRow.notificationConfig;

    return {
        formData: {
            clientName: savedConfig.clientName ?? planRow.clientName ?? "",
            ...mergeSavedNotificationConfigWithDefaults(
                savedConfig,
                defaultCatalog,
            ),
        },
        planBasicInfo,
    };
};

/**
 * Maps auto-populated read-only notification fields for the Variables tab.
 * @param {Object} [planRow] - Selected plan grid row.
 * @param {ReadonlyArray<object>} [administratorNameOptions] - Hydrated administrator options.
 * @returns {{ planName: string, phoneNumberMemberServices: string, supportHours: string, portalUrl: string, textCode: string, textWord: string }}
 */
export const mapPlanRowToAutoPopulatedFields = (
    planRow,
    administratorNameOptions,
) => {
    const administratorFields = getAdministratorDerivedFields(
        administratorNameOptions,
    );

    return {
        planName: planRow?.planName ?? "",
        phoneNumberMemberServices: formatMemberServicesPhone(
            planRow?.phoneNumberMemberServices,
        ),
        supportHours: planRow?.supportHours ?? "",
        portalUrl: planRow?.portalUrl ?? "",
        textCode: administratorFields.textCode,
        textWord: administratorFields.textWord,
    };
};

export const applyEoyChangeToEvents = (
    commsLibraryOptions,
    { eventTitle, eventType, isEoy },
) =>
    commsLibraryOptions.map((library) => {
        if (!library.isSelected) {
            return library;
        }

        return {
            ...library,
            events: library.events.map((event) => {
                if (
                    event.title !== eventTitle ||
                    event.type !== eventType
                ) {
                    return event;
                }

                const nextEvent = { ...event, isEOY: isEoy };

                if (isEoy) {
                    nextEvent.defaultEn = `${String(event.defaultEn).replace(/_EOY$/u, "")}_EOY`;
                    nextEvent.defaultEnSp = `${String(event.defaultEnSp).replace(/_EOY$/u, "")}_EOY`;
                } else {
                    nextEvent.defaultEn = String(event.defaultEn).replace(
                        /_EOY$/u,
                        "",
                    );
                    nextEvent.defaultEnSp = String(event.defaultEnSp).replace(
                        /_EOY$/u,
                        "",
                    );
                }

                return nextEvent;
            }),
        };
    });

// --- from planSetupUtils.js ---

/**
 * Plan Manager Plan Setup wizard pure helpers.
 *
 * Holds the interaction-grammar logic for the setup wizard (which steps are
 * visible for the current adjudication platform and what the forward button
 * should read) so the presentational components stay free of pure computation.
 */

/**
 * Adjudication platform a plan is configured against. Javelina plans expose the
 * Accumulators step; HRP plans source accumulators from HRP directly, so that
 * step is hidden.
 * @readonly
 * @enum {string}
 */
export const PlanSetupPlatform = Object.freeze({
    JAVELINA: "Javelina",
    HRP: "HRP",
});

/**
 * Stable identifier for the Accumulators step, used to drop it from the visible
 * step list when a plan is configured against HRP.
 * @constant {string}
 */
export const ACCUMULATORS_STEP_ID = "accumulators";

/**
 * Forward-button labels for the wizard navigation, in the order they appear as
 * the user advances through and then verifies the final step.
 * @readonly
 * @enum {string}
 */
export const PlanSetupNextButtonLabel = Object.freeze({
    NEXT: "Next",
    VERIFY: "Verify",
    SUBMIT: "Submit",
});

/**
 * Returns the visible wizard steps for the given platform. The Accumulators step
 * is dropped for HRP plans; every other step is preserved in order.
 * @param {ReadonlyArray<{ id: string, label: string }>} steps - All candidate steps in display order.
 * @param {boolean} isHrp - Whether the plan is configured against HRP.
 * @returns {Array<{ id: string, label: string }>} Steps to render, in order.
 */
export const getVisiblePlanSetupSteps = (steps, isHrp) => {
    const candidateSteps = steps ?? [];

    if (!isHrp) {
        return candidateSteps.slice();
    }

    return candidateSteps.filter((step) => step.id !== ACCUMULATORS_STEP_ID);
};

/**
 * Returns the forward-button label for the wizard given where the user is and
 * whether they have already verified the final step.
 *
 * On any step before the last, the button reads Next. On the last content step
 * it reads Verify until the user has verified, after which it reads Submit.
 * @param {Object} params
 * @param {number} params.activeStep - Current step, 1-based (matches the Stepper).
 * @param {number} params.totalSteps - Total number of visible steps.
 * @param {boolean} params.hasVerified - Whether the final step has been verified.
 * @returns {string} The label to render on the forward button.
 */
export const getPlanSetupNextButtonLabel = ({
    activeStep,
    totalSteps,
    hasVerified,
}) => {
    const isOnLastStep = activeStep >= totalSteps;

    if (!isOnLastStep) {
        return PlanSetupNextButtonLabel.NEXT;
    }

    return hasVerified
        ? PlanSetupNextButtonLabel.SUBMIT
        : PlanSetupNextButtonLabel.VERIFY;
};

/**
 * Whether clicking the forward button on the final step should open the submit
 * confirmation modal (true only once the user has verified).
 * @param {Object} params
 * @param {number} params.activeStep - Current step, 1-based.
 * @param {number} params.totalSteps - Total number of visible steps.
 * @param {boolean} params.hasVerified - Whether the final step has been verified.
 * @returns {boolean} True when the forward action should open the confirm modal.
 */
export const shouldOpenSubmitConfirmation = ({
    activeStep,
    totalSteps,
    hasVerified,
}) => activeStep >= totalSteps && hasVerified;

/**
 * Stable wizard step ids used by validation and completion helpers.
 * @readonly
 * @enum {string}
 */
export const PlanSetupStepId = Object.freeze({
    PLAN_DESIGN: "planDesign",
    VENDOR_DETAILS: "vendorDetails",
    MEMBER_COSTS: "memberCosts",
    ACCUMULATORS: ACCUMULATORS_STEP_ID,
    SETUP: "setup",
});

/**
 * @param {*} value
 * @returns {boolean}
 */
const isBlank = (value) => String(value ?? "").trim() === "";

/**
 * Calculates the initial date the datepicker popup should view when opened.
 * Prevents viewing months where all dates are disabled by minDate or maxDate.
 *
 * @param {Date | null} [selectedDate] - Currently selected date or null.
 * @param {Date | null} [minDate] - Minimum selectable date.
 * @param {Date | null} [maxDate] - Maximum selectable date.
 * @param {Date} [referenceDate] - Fallback date when selectedDate is null (defaults to new Date()).
 * @returns {Date} The target Date object the calendar should display.
 */
export const calculateInitialOpenDate = (
    selectedDate,
    minDate,
    maxDate,
    referenceDate = new Date(),
) => {
    const isDateValid = (date) =>
        date instanceof Date && !Number.isNaN(date.getTime());

    const validSelected = isDateValid(selectedDate) ? selectedDate : null;
    const validMin = isDateValid(minDate) ? minDate : null;
    const validMax = isDateValid(maxDate) ? maxDate : null;
    const validReference = isDateValid(referenceDate)
        ? referenceDate
        : new Date();

    if (validSelected) {
        if (validMin && validSelected < validMin) {
            return validMin;
        }
        if (validMax && validSelected > validMax) {
            return validMax;
        }
        return validSelected;
    }

    if (validMin && validReference < validMin) {
        return validMin;
    }
    if (validMax && validReference > validMax) {
        return validMax;
    }

    return validReference;
};

/**
 * Reconciles plan design date fields when the start date changes.
 * If the new start date is equal to or greater than the current end date,
 * clears the stale end date to prevent maintaining an invalid range.
 *
 * @param {object} planDesignValues - Current plan design form values.
 * @param {string} newStartDate - New start date in MM-DD-YYYY format.
 * @returns {object} Updated plan design slice with reconciled dates.
 */
export const reconcilePlanDesignDatesOnStartChange = (
    planDesignValues,
    newStartDate,
) => {
    const values = planDesignValues ?? {};
    const startTimestamp = parseMmDdYyyyTimestamp(newStartDate);
    const endTimestamp = parseMmDdYyyyTimestamp(values.endDate);

    if (
        startTimestamp != null &&
        endTimestamp != null &&
        startTimestamp >= endTimestamp
    ) {
        return {
            ...values,
            startDate: newStartDate,
            endDate: "",
        };
    }

    return {
        ...values,
        startDate: newStartDate,
    };
};

/**
 * Parses an MM-DD-YYYY or MM/DD/YYYY string into a comparable timestamp, or null when invalid.
 * @param {string} value
 * @returns {number | null}
 */
const parseMmDdYyyyTimestamp = (value) => {
    if (isBlank(value)) {
        return null;
    }

    const parts = String(value).split(/[-/]/);
    if (parts.length !== 3) {
        return null;
    }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);

    if (
        !Number.isInteger(month) ||
        !Number.isInteger(day) ||
        !Number.isInteger(year)
    ) {
        return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    if (
        parsedDate.getFullYear() !== year ||
        parsedDate.getMonth() !== month - 1 ||
        parsedDate.getDate() !== day
    ) {
        return null;
    }

    return parsedDate.getTime();
};

/**
 * Masks a phone number string as digits are typed in ###-###-#### format matching plan-management-client, max 10 digits (12 chars).
 * @param {string | undefined} value
 * @returns {string}
 */
export const maskPhoneNumber = (value) => {
    if (!value) {
        return "";
    }

    const digits = String(value).replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Checks whether a phone number matches standard 10-digit or XXX-XXX-XXXX format.
 * @param {*} value
 * @returns {boolean}
 */
export const isValidPhoneNumber = (value) =>
    /^(\d{3}-\d{3}-\d{4}|\d{10})$/.test(String(value ?? "").trim());

/**
 * Canonical validation guidance and error copy for Plan Design step matching plan-management-client.
 * @readonly
 * @type {Record<string, string>}
 */
export const PLAN_DESIGN_VALIDATION_MESSAGES = Object.freeze({
    PLAN_NAME: "Please enter a plan name",
    START_DATE_INVALID: "Please enter a valid date.",
    END_DATE_INVALID: "Please enter a valid date.",
    END_DATE_AFTER_START: "End date must be greater than start date",
    PLAN_TYPE: "Please select a plan type",
    CMS_ID: "Please enter a cmsId",
    CMS_NAME: "Please enter a CMS name",
    CONTACT_NUMBER: "Please enter a valid phone number",
    SUPPORT_HOURS: "Please enter Member Care contact hours",
    SENDER_EMAIL: "Please enter a sender email",
    PORTAL_URL: "Please enter a portal URL",
    PCP_DESIGNATION: "Please select a pcp designation option",
    VPC_ENABLED: "Please select a virtual primary care option",
    REFERRALS: "Please select a referral option",
    REFERRAL_TYPE: "Please select a referral type",
    MFA: "Please select a MFA option",
    PASSWORD_HELPER_TEXT: "Please enter a valid message",
    MIN_CHARACTERS_TOO_SHORT: "The minimum number of characters must be at least 8",
    MIN_CHARACTERS_SUM_EXCEEDED: "Minimum number of characters must be higher than the sum of the requirements",
    SPECIAL_CHARACTERS_INVALID: "Please enter a valid number of special characters",
    LETTERS_INVALID: "Please enter a valid number of letters",
    NUMBERS_INVALID: "Please enter a valid number of numbers",
});

/**
 * @param {object} values
 * @param {ReadonlyArray<{ field: string, message: string }>} requiredFields
 * @returns {Record<string, string>}
 */
const collectRequiredFieldErrors = (values, requiredFields) => {
    const errors = {};

    for (const { field, message } of requiredFields) {
        if (isBlank(values?.[field])) {
            errors[field] = message;
        }
    }

    return errors;
};

/**
 * Validates the Plan Design step.
 * @param {object} planDesign
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export const validatePlanDesignStep = (planDesign = {}) => {
    const errors = {};

    if (isBlank(planDesign.planName)) {
        errors.planName = PLAN_DESIGN_VALIDATION_MESSAGES.PLAN_NAME;
    }

    const startTimestamp = parseMmDdYyyyTimestamp(planDesign.startDate);
    if (startTimestamp == null) {
        errors.startDate = PLAN_DESIGN_VALIDATION_MESSAGES.START_DATE_INVALID;
    }

    const endTimestamp = parseMmDdYyyyTimestamp(planDesign.endDate);
    if (endTimestamp == null) {
        errors.endDate = PLAN_DESIGN_VALIDATION_MESSAGES.END_DATE_INVALID;
    } else if (startTimestamp != null && endTimestamp <= startTimestamp) {
        errors.endDate = PLAN_DESIGN_VALIDATION_MESSAGES.END_DATE_AFTER_START;
    }

    if (isBlank(planDesign.planType)) {
        errors.planType = PLAN_DESIGN_VALIDATION_MESSAGES.PLAN_TYPE;
    }

    if (isBlank(planDesign.cmsId)) {
        errors.cmsId = PLAN_DESIGN_VALIDATION_MESSAGES.CMS_ID;
    }

    if (isBlank(planDesign.cmsName)) {
        errors.cmsName = PLAN_DESIGN_VALIDATION_MESSAGES.CMS_NAME;
    }

    if (
        isBlank(planDesign.contactNumber) ||
        !isValidPhoneNumber(planDesign.contactNumber)
    ) {
        errors.contactNumber = PLAN_DESIGN_VALIDATION_MESSAGES.CONTACT_NUMBER;
    }

    if (isBlank(planDesign.supportHours)) {
        errors.supportHours = PLAN_DESIGN_VALIDATION_MESSAGES.SUPPORT_HOURS;
    }

    if (isBlank(planDesign.senderEmail)) {
        errors.senderEmail = PLAN_DESIGN_VALIDATION_MESSAGES.SENDER_EMAIL;
    }

    if (isBlank(planDesign.portalUrl)) {
        errors.portalUrl = PLAN_DESIGN_VALIDATION_MESSAGES.PORTAL_URL;
    }

    if (isBlank(planDesign.pcpDesignation)) {
        errors.pcpDesignation = PLAN_DESIGN_VALIDATION_MESSAGES.PCP_DESIGNATION;
    }

    if (
        planDesign.vpcEnabled === undefined ||
        planDesign.vpcEnabled === null ||
        planDesign.vpcEnabled === ""
    ) {
        errors.vpcEnabled = PLAN_DESIGN_VALIDATION_MESSAGES.VPC_ENABLED;
    }

    if (isBlank(planDesign.referrals)) {
        errors.referrals = PLAN_DESIGN_VALIDATION_MESSAGES.REFERRALS;
    } else if (
        planDesign.referrals === "yes" &&
        isBlank(planDesign.referralType)
    ) {
        errors.referralType = PLAN_DESIGN_VALIDATION_MESSAGES.REFERRAL_TYPE;
    }

    if (isBlank(planDesign.mfa)) {
        errors.mfa = PLAN_DESIGN_VALIDATION_MESSAGES.MFA;
    }

    const specialNum = Number(planDesign.specialCharactersNo);
    if (
        isBlank(planDesign.specialCharactersNo) ||
        isNaN(specialNum) ||
        specialNum <= 0
    ) {
        errors.specialCharactersNo =
            PLAN_DESIGN_VALIDATION_MESSAGES.SPECIAL_CHARACTERS_INVALID;
    }

    const lettersNum = Number(planDesign.lettersNo);
    if (
        isBlank(planDesign.lettersNo) ||
        isNaN(lettersNum) ||
        lettersNum <= 0
    ) {
        errors.lettersNo = PLAN_DESIGN_VALIDATION_MESSAGES.LETTERS_INVALID;
    }

    const numbersNum = Number(planDesign.numbersNo);
    if (
        isBlank(planDesign.numbersNo) ||
        isNaN(numbersNum) ||
        numbersNum <= 0
    ) {
        errors.numbersNo = PLAN_DESIGN_VALIDATION_MESSAGES.NUMBERS_INVALID;
    }

    const minCharactersNum = Number(planDesign.minCharactersNo);
    const validSpec = !isNaN(specialNum) && specialNum > 0 ? specialNum : 0;
    const validLetters = !isNaN(lettersNum) && lettersNum > 0 ? lettersNum : 0;
    const validNumbers = !isNaN(numbersNum) && numbersNum > 0 ? numbersNum : 0;
    const requirementsSum = validSpec + validLetters + validNumbers;

    if (
        isBlank(planDesign.minCharactersNo) ||
        isNaN(minCharactersNum) ||
        minCharactersNum < 8
    ) {
        errors.minCharactersNo =
            PLAN_DESIGN_VALIDATION_MESSAGES.MIN_CHARACTERS_TOO_SHORT;
    } else if (requirementsSum > minCharactersNum) {
        errors.minCharactersNo =
            PLAN_DESIGN_VALIDATION_MESSAGES.MIN_CHARACTERS_SUM_EXCEEDED;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Ensures a URL starts with https:// if non-empty, matching plan-management-client checkURLInput.
 * @param {*} url
 * @returns {string}
 */
export const ensureHttpsPrefix = (url) => {
    if (!url || typeof url !== "string") {
        return "";
    }

    const trimmed = url.trim();
    if (!trimmed) {
        return "";
    }

    if (!trimmed.startsWith("https://")) {
        return `https://${trimmed.replace(/^http:\/\//, "")}`;
    }

    return trimmed;
};

/**
 * Canonical validation messages for Vendor Details step matching plan-management-client.
 * @readonly
 * @type {Record<string, string>}
 */
export const VENDOR_DETAILS_VALIDATION_MESSAGES = Object.freeze({
    TELEMEDICINE_REQUIRED: "Please select a telemedicine option",
    TELEMEDICINE_CUSTOM_REQUIRED: "Please enter a telemedicine provider",
    PBM_REQUIRED: "Please select a PBM option",
});

/**
 * Validates the Vendor Details step.
 * @param {object} vendorDetails
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export const validateVendorDetailsStep = (vendorDetails = {}) => {
    const errors = {};

    if (isBlank(vendorDetails?.telemedicine)) {
        errors.telemedicine =
            VENDOR_DETAILS_VALIDATION_MESSAGES.TELEMEDICINE_REQUIRED;
    } else if (
        vendorDetails?.telemedicine === "Other" &&
        isBlank(vendorDetails?.telemedicineCustom)
    ) {
        errors.telemedicineCustom =
            VENDOR_DETAILS_VALIDATION_MESSAGES.TELEMEDICINE_CUSTOM_REQUIRED;
    }

    if (isBlank(vendorDetails?.pbmName)) {
        errors.pbmName = VENDOR_DETAILS_VALIDATION_MESSAGES.PBM_REQUIRED;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Canonical validation messages for Accumulators step matching plan-management-client.
 * @readonly
 * @type {Record<string, string>}
 */
export const ACCUMULATORS_VALIDATION_MESSAGES = Object.freeze({
    CATEGORY_REQUIRED: "Category is required",
    PLAN_YEAR_REQUIRED: "Plan year is required",
    DEDUCTIBLE_TYPE_REQUIRED: "Deductible type is required",
    IN_NETWORK_LABEL_REQUIRED: "In-network label is required",
    OUT_OF_NETWORK_LABEL_REQUIRED: "Out-of-network label is required",
});

/**
 * Validates the Accumulators step (Javelina only).
 * @param {object} accumulators
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export const validateAccumulatorsStep = (accumulators = {}) => {
    const errors = collectRequiredFieldErrors(accumulators, [
        {
            field: "category",
            message: ACCUMULATORS_VALIDATION_MESSAGES.CATEGORY_REQUIRED,
        },
        {
            field: "planYear",
            message: ACCUMULATORS_VALIDATION_MESSAGES.PLAN_YEAR_REQUIRED,
        },
        {
            field: "deductibleType",
            message: ACCUMULATORS_VALIDATION_MESSAGES.DEDUCTIBLE_TYPE_REQUIRED,
        },
        {
            field: "inNetworkLabel",
            message: ACCUMULATORS_VALIDATION_MESSAGES.IN_NETWORK_LABEL_REQUIRED,
        },
        {
            field: "outOfNetworkLabel",
            message:
                ACCUMULATORS_VALIDATION_MESSAGES.OUT_OF_NETWORK_LABEL_REQUIRED,
        },
    ]);

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

/**
 * Validates the Setup (document upload) step. SPD and SBC are optional and
 * independent — neither is required to complete the step.
 * @param {object} [_setup]
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export const validateSetupDocumentsStep = (_setup = {}) => ({
    isValid: true,
    errors: {},
});

/**
 * Validates a single plan-setup wizard step.
 * @param {string} stepId
 * @param {object} form
 * @returns {{ isValid: boolean, errors: Record<string, string> }}
 */
export const validatePlanSetupStep = (stepId, form = {}) => {
    switch (stepId) {
        case PlanSetupStepId.PLAN_DESIGN:
            return validatePlanDesignStep(form.planDesign);
        case PlanSetupStepId.VENDOR_DETAILS:
            return validateVendorDetailsStep(form.vendorDetails);
        case PlanSetupStepId.MEMBER_COSTS:
            return validateMemberCostsStep(form.memberCosts);
        case PlanSetupStepId.ACCUMULATORS:
            return validateAccumulatorsStep(form.accumulators);
        case PlanSetupStepId.SETUP:
            return validateSetupDocumentsStep(form.setup);
        default:
            return { isValid: true, errors: {} };
    }
};

/**
 * Validates every visible wizard step and returns the first invalid step.
 * @param {ReadonlyArray<{ id: string, label?: string }>} visibleSteps
 * @param {object} form
 * @returns {{
 *   isValid: boolean,
 *   firstInvalidStepIndex: number,
 *   firstInvalidStepId: string | null,
 *   stepResults: Record<string, { isValid: boolean, errors: Record<string, string> }>
 * }}
 */
export const validateAllPlanSetupSteps = (visibleSteps = [], form = {}) => {
    const stepResults = {};
    let firstInvalidStepIndex = -1;
    let firstInvalidStepId = null;

    visibleSteps.forEach((step, index) => {
        const result = validatePlanSetupStep(step.id, form);
        stepResults[step.id] = result;

        if (!result.isValid && firstInvalidStepIndex < 0) {
            firstInvalidStepIndex = index;
            firstInvalidStepId = step.id;
        }
    });

    return {
        isValid: firstInvalidStepIndex < 0,
        firstInvalidStepIndex,
        firstInvalidStepId,
        stepResults,
    };
};

/**
 * Annotates visible steps with live completion flags for the stepper.
 * Unvisited / untouched steps remain complete (green) until they are evaluated.
 *
 * @param {ReadonlyArray<{ id: string, label: string }>} visibleSteps
 * @param {object} form
 * @param {Iterable<string> | Set<string> | null | undefined} [evaluatedStepIds]
 * @returns {Array<{ id: string, label: string, isComplete: boolean }>}
 */
export const annotatePlanSetupStepsWithCompletion = (
    visibleSteps = [],
    form = {},
    evaluatedStepIds = null,
) => {
    const evaluatedSet =
        evaluatedStepIds instanceof Set
            ? evaluatedStepIds
            : Array.isArray(evaluatedStepIds)
              ? new Set(evaluatedStepIds)
              : null;

    return visibleSteps.map((step) => {
        const isEvaluated = evaluatedSet ? evaluatedSet.has(step.id) : true;
        const isValid = validatePlanSetupStep(step.id, form).isValid;
        const isComplete = isEvaluated ? isValid : true;

        return {
            ...step,
            isComplete,
        };
    });
};

const DEFAULT_SENDER_EMAIL = "The Centivo Team <No_Reply@centivo.com>";

/**
 * Display options for the accumulator plan year select (2018 through 2050).
 * Matches legacy plan-management-client: [...Array(33).keys()].map(x => x + 2018).
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const ACCUMULATOR_PLAN_YEAR_OPTIONS = Object.freeze(
    Array.from({ length: 33 }, (_, i) => String(2018 + i)),
);

/**
 * Strips non-digit characters from an accumulator amount value.
 * @param {string | number | undefined | null} val
 * @returns {string}
 */
export const sanitizeAccumulatorNumeric = (val) => {
    if (val === undefined || val === null) {
        return "";
    }
    return String(val).replace(/[^0-9]/g, "");
};

/**
 * Formats a numeric value with thousands commas (e.g. "1500" -> "1,500").
 * @param {string | number | undefined | null} val
 * @returns {string}
 */
export const formatAccumulatorCurrency = (val) => {
    const digitsOnly = sanitizeAccumulatorNumeric(val);
    if (!digitsOnly) {
        return "";
    }
    return digitsOnly.replace(/(\d)(?=(\d{3})+$)/g, "$1,");
};

/**
 * Determines whether an accumulator amount field has a positive numerical value.
 * @param {string | number | undefined | null} val
 * @returns {boolean}
 */
export const hasAccumulatorPositiveValue = (val) => {
    const digitsOnly = sanitizeAccumulatorNumeric(val);
    return digitsOnly !== "" && Number(digitsOnly) > 0;
};

/**
 * Display options for the accumulator network-tier label selects. Labels are
 * stored uppercased on the wire.
 * @readonly
 * @type {Readonly<{ inNetworkLabel: ReadonlyArray<string>, outOfNetworkLabel: ReadonlyArray<string> }>}
 */
export const ACCUMULATOR_NETWORK_LABEL_OPTIONS = Object.freeze({
    inNetworkLabel: Object.freeze(["In-Network", "Coordinated", "N/A"]),
    outOfNetworkLabel: Object.freeze(["Out-of-Network", "Uncoordinated", "N/A"]),
});

/**
 * Maps a stored (uppercased) network label back to its display option.
 * Unknown values are returned unchanged.
 * @param {"inNetworkLabel" | "outOfNetworkLabel"} field
 * @param {string} value
 * @returns {string}
 */
export const normalizeAccumulatorNetworkLabel = (field, value) => {
    const text = value == null ? "" : String(value);
    const match = ACCUMULATOR_NETWORK_LABEL_OPTIONS[field]?.find(
        (option) => option.toUpperCase() === text.toUpperCase(),
    );
    return match ?? text;
};

const EMPTY_ACCUMULATORS = Object.freeze({
    category: "",
    planYear: "",
    deductibleType: "",
    inNetworkLabel: "",
    outOfNetworkLabel: "",
    inNetworkDeductiblePatientAmount: "",
    outOfNetworkDeductiblePatientAmount: "",
    inNetworkDeductibleFamilyAmount: "",
    outOfNetworkDeductibleFamilyAmount: "",
    inNetworkOutOfPocketPatientMaxAmount: "",
    outOfNetworkOutOfPocketPatientMaxAmount: "",
    inNetworkOutOfPocketFamilyMaxAmount: "",
    outOfNetworkOutOfPocketFamilyMaxAmount: "",
    inNetworkDeductiblePatientAmountEnabled: false,
    outOfNetworkDeductiblePatientAmountEnabled: false,
    inNetworkDeductibleFamilyAmountEnabled: false,
    outOfNetworkDeductibleFamilyAmountEnabled: false,
    inNetworkOutOfPocketPatientMaxAmountEnabled: false,
    outOfNetworkOutOfPocketPatientMaxAmountEnabled: false,
    inNetworkOutOfPocketFamilyMaxAmountEnabled: false,
    outOfNetworkOutOfPocketFamilyMaxAmountEnabled: false,
});

/**
 * Returns a blank wizard form for runtime loading and empty states.
 * @returns {object} Deep-cloned empty plan setup form.
 */
export const createEmptyPlanSetupForm = () => ({
    planDesign: {
        planName: "",
        startDate: "",
        endDate: "",
        planType: "",
        fullDirectory: false,
        cmsId: "",
        cmsName: "",
        contactNumber: "",
        supportHours: "",
        senderEmail: "",
        portalUrl: "",
        pcpDesignation: "",
        vpcEnabled: false,
        referrals: "no",
        referralType: "",
        mfa: "Forced",
        passwordHelperText: "",
        minCharactersNo: 8,
        specialCharactersNo: 1,
        lettersNo: 1,
        numbersNo: 1,
    },
    vendorDetails: {
        telemedicine: "",
        pbmName: "",
        pbmSiteURL: "",
        formularyUrl: "",
    },
    memberCosts: {
        tier1: "In-network",
        tier2: "Out-of-network",
        includeUnguidedColumn: false,
        unguidedTierLabel: "",
        medicalRows: [],
        rxHeader1: "",
        rxHeader2: "",
        rxRows: [],
        rxSpecialtyHeader1: "",
        rxSpecialtyHeader2: "",
        rxSpecialtyRows: [],
    },
    accumulators: { ...EMPTY_ACCUMULATORS },
    setup: {
        activeDocType: "spd",
        spdFileName: "",
        sbcFileName: "",
        spdUrl: "",
        sbcUrl: "",
    },
});

/**
 * Maps getAllSettings response into dropdown option lists for Plan Setup.
 * @param {object | null | undefined} settings
 * @returns {{ planTierNames: string[], rbrNetworkNames: string[], thirdPartyNetworkNames: string[], telemedicineOptions: string[], pbmNames: string[] }}
 */
export const mapSettingsToSetupOptions = (settings) => {
    const source = settings ?? {};

    return {
        planTierNames: Array.isArray(source.planTierNames)
            ? source.planTierNames
            : [],
        rbrNetworkNames: Array.isArray(source.networkNames?.RBR)
            ? source.networkNames.RBR
            : [],
        thirdPartyNetworkNames: Array.isArray(
            source.networkNames?.["Third-party"],
        )
            ? source.networkNames["Third-party"]
            : [],
        telemedicineOptions: Array.isArray(source.telemedicine)
            ? source.telemedicine
            : [],
        pbmNames: Array.isArray(source.pbmNames) ? source.pbmNames : [],
    };
};

/**
 * @param {string | Date | null | undefined} dateValue
 * @returns {string}
 */
export const formatPlanDateToMmDdYyyy = (dateValue) => {
    if (dateValue == null || dateValue === "") {
        return "";
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return String(dateValue);
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${month}-${day}-${year}`;
};

/**
 * @param {boolean | undefined} pcpRequired
 * @returns {string}
 */
export const mapPcpDesignationFromPlan = (pcpRequired) => {
    if (pcpRequired === undefined || pcpRequired === null) {
        return "";
    }

    return pcpRequired === true ? "required" : "none";
};

/**
 * @param {string | undefined} mfaPasswordRequirements
 * @returns {{ minCharactersNo: number, specialCharactersNo: number, lettersNo: number, numbersNo: number }}
 */
export const parsePasswordRequirementsFromRegex = (mfaPasswordRequirements) => {
    const defaultRequirements = {
        minCharactersNo: 8,
        specialCharactersNo: 1,
        lettersNo: 1,
        numbersNo: 1,
    };

    if (!mfaPasswordRequirements) {
        return defaultRequirements;
    }

    const parseRequirementCount = (pattern) => {
        const splitResult = mfaPasswordRequirements.split(/[()]+/);
        const matchingSegment = splitResult.find((segment) =>
            segment.includes(pattern),
        );

        if (!matchingSegment) {
            return 0;
        }

        const digitMatch = matchingSegment.match(/\d+/g);
        return digitMatch ? Number(digitMatch[0]) : 1;
    };

    return {
        minCharactersNo:
            parseRequirementCount("[A-Za-z\\d@$!%*#?&]") ||
            defaultRequirements.minCharactersNo,
        specialCharactersNo:
            parseRequirementCount("[@$!%*#?&]") ||
            defaultRequirements.specialCharactersNo,
        lettersNo:
            parseRequirementCount("[A-Z]") || defaultRequirements.lettersNo,
        numbersNo:
            parseRequirementCount("\\d") || defaultRequirements.numbersNo,
    };
};

/**
 * @param {object} planData
 * @returns {object}
 */
export const mapPlanDesign = (planData) => {
    const passwordRequirements = parsePasswordRequirementsFromRegex(
        planData.mfaPasswordRequirements,
    );

    const planType = planData.planType ?? "";
    const isOpenAccess = planType === "OpenAccess";
    const hasSavedMfa = Boolean(planData.mfa);
    const isFirstTimeConfig = !planData.completed && !planData.started;
    const mfa = hasSavedMfa
        ? planData.mfa
        : isFirstTimeConfig
          ? "Forced"
          : (planData.mfa ?? "");

    return {
        planName: planData.planName ?? "",
        startDate: formatPlanDateToMmDdYyyy(planData.planStartDate),
        endDate: formatPlanDateToMmDdYyyy(planData.planEndDate),
        planType,
        fullDirectory: isOpenAccess ? Boolean(planData.fullDirectory) : false,
        cmsId: planData.cmsId ?? "",
        cmsName: planData.networkName ?? "",
        contactNumber: formatMemberServicesPhone(
            planData.phoneNumberMemberServices,
        ),
        supportHours: planData.supportHours ?? "",
        senderEmail: planData.senderEmail ?? DEFAULT_SENDER_EMAIL,
        portalUrl: planData.portalUrl ?? "",
        pcpDesignation: mapPcpDesignationFromPlan(planData.pcpRequired),
        vpcEnabled: Boolean(planData.virtualPrimaryCareEnabled),
        referrals: planData.referralRequired ?? "no",
        referralType: planData.referralAuthType ?? "",
        mfa,
        passwordHelperText: planData.passwordHelperText ?? "",
        minCharactersNo: passwordRequirements.minCharactersNo,
        specialCharactersNo: passwordRequirements.specialCharactersNo,
        lettersNo: passwordRequirements.lettersNo,
        numbersNo: passwordRequirements.numbersNo,
    };
};

/**
 * @param {object} planData
 * @returns {object}
 */
export const mapVendorDetails = (planData) => ({
    telemedicine: planData.telemedicine ?? "",
    pbmName: planData.pbmName ?? "",
    pbmSiteURL: planData.pbmLink ?? "",
    formularyUrl: planData.formularyUrl ?? "",
});

/**
 * @param {Array<{ category?: string }>} familyMedicalCoverageList
 * @param {string} category
 * @returns {boolean}
 */
const isMemberCostCategoryChecked = (familyMedicalCoverageList, category) =>
    familyMedicalCoverageList.some((entry) => entry.category === category);

/**
 * @param {Array<object>} rxCoverageList
 * @returns {{ rxHeader1: string, rxHeader2: string, rxRows: object[] }}
 */
const mapRxCoverageTable = (rxCoverageList) => {
    if (!Array.isArray(rxCoverageList) || rxCoverageList.length <= 1) {
        return { rxHeader1: "", rxHeader2: "", rxRows: [] };
    }

    const [headerRow, ...dataRows] = rxCoverageList;

    return {
        rxHeader1: headerRow?.row?.[1] ?? "",
        rxHeader2: headerRow?.row?.[2] ?? "",
        rxRows: dataRows.map((record, index) => ({
            id: `rx-${index}`,
            category: record.row?.[0] ?? "",
            userInput1: record.row?.[1] ?? "",
            userInput2: record.row?.[2] ?? "",
            isChecked: record.row?.[3] ?? true,
        })),
    };
};

export const DEFAULT_UNGUIDED_TIER_LABEL = "Unguided (In-Network)";

export const MEMBER_COSTS_VALIDATION_MESSAGES = Object.freeze({
    TIER_1_REQUIRED: "Please enter a plan tier 1",
    TIER_SLASH: "Field cannot contain forward slashes.",
    TIER_NOT_UNIQUE: "Please enter a unique label for each tier",
    CATEGORY_REQUIRED_IF_CHECKED:
        "Field cannot be empty if the category is checked off.",
    UNGUIDED_LABEL_REQUIRED: "Please enter an Unguided tier label",
    UNGUIDED_LABEL_NO_SLASH: "Field cannot contain forward slashes.",
    UNGUIDED_LABEL_NOT_UNIQUE: "Please enter a unique label for each tier",
});

/**
 * @param {object | null | undefined} row
 * @returns {string}
 */
export const resolveRowUnguidedAmountFromStoredRow = (row) => {
    const candidates = [
        row?.unguidedInNetwork,
        row?.individualUnguided,
        row?.familyUnguided,
    ];

    for (const value of candidates) {
        if (value !== undefined && value !== null && value !== "") {
            return String(value);
        }
    }

    return "";
};

/**
 * @param {object | null | undefined} planData
 * @returns {boolean}
 */
export const shouldRestoreUnguidedColumn = (planData) => {
    if (!planData) {
        return false;
    }

    if (planData.hasUnguidedInNetwork) {
        return true;
    }

    const storedLabel = String(planData.unguidedTierLabel ?? "").trim();
    if (storedLabel.length > 0) {
        return true;
    }

    const rows = planData.fullMedicalCoverageList ?? [];
    return rows.some((row) => {
        const amount = resolveRowUnguidedAmountFromStoredRow(row);
        return String(amount).trim().length > 0;
    });
};

/**
 * @param {object | null | undefined} planData
 * @returns {{ tier1: string, tier2: string, unguidedTierLabel: string, includeUnguidedColumn: boolean }}
 */
export const mapStoredPlanTierToMemberCostHeaders = (planData) => {
    const planTier = planData?.planTier;
    const hasUnguidedInNetwork = Boolean(planData?.hasUnguidedInNetwork);
    const storedUnguidedLabel = String(planData?.unguidedTierLabel ?? "").trim();

    if (planTier === undefined || planTier === null || planTier === "") {
        return {
            tier1: "In-network",
            tier2: "Out-of-network",
            unguidedTierLabel: "",
            includeUnguidedColumn: false,
        };
    }

    const includeUnguidedColumn = shouldRestoreUnguidedColumn(planData);

    if (typeof planTier !== "string") {
        return {
            tier1: "",
            tier2: "",
            unguidedTierLabel: "",
            includeUnguidedColumn,
        };
    }

    const segments = planTier.split("/");
    const segment1 = segments[0]?.trim() ?? "";
    const segment2 = segments[1]?.trim() ?? "";
    const segment3 = segments[2]?.trim() ?? "";

    const publishedUnguidedLabel =
        hasUnguidedInNetwork && segment2 ? segment2 : "";

    let unguidedTierLabel = "";
    if (publishedUnguidedLabel) {
        unguidedTierLabel = publishedUnguidedLabel;
    } else if (storedUnguidedLabel) {
        unguidedTierLabel = storedUnguidedLabel;
    } else if (includeUnguidedColumn) {
        unguidedTierLabel = DEFAULT_UNGUIDED_TIER_LABEL;
    }

    let tier1 = segment1;
    let tier2 = "";

    if (hasUnguidedInNetwork) {
        tier2 = segment3;
    } else if (segments.length >= 3 && includeUnguidedColumn) {
        tier1 = segment1;
        tier2 = segment2;
    } else {
        tier2 = segment2;
    }

    return {
        tier1,
        tier2,
        unguidedTierLabel,
        includeUnguidedColumn,
    };
};

/**
 * @param {object | null | undefined} memberCosts
 * @returns {boolean}
 */
export const shouldPublishUnguidedAmounts = (memberCosts) => {
    if (!memberCosts?.includeUnguidedColumn) {
        return false;
    }

    const trimmedLabel = String(memberCosts.unguidedTierLabel ?? "").trim();
    if (!trimmedLabel) {
        return false;
    }

    const medicalRows = memberCosts.medicalRows ?? [];
    return medicalRows.some(
        (row) =>
            row.isChecked &&
            String(row.unguidedInNetwork ?? "").trim().length > 0,
    );
};

/**
 * @param {object | null | undefined} memberCosts
 * @returns {{ isValid: boolean, errors: Record<string, string>, unguidedTierLabelError: string }}
 */
export const validateMemberCostsStep = (memberCosts = {}) => {
    const errors = {};
    let unguidedTierLabelError = "";

    const tier1 = String(memberCosts?.tier1 ?? "").trim();
    const tier2 = String(memberCosts?.tier2 ?? "").trim();

    if (!tier1) {
        errors.tier1 = MEMBER_COSTS_VALIDATION_MESSAGES.TIER_1_REQUIRED;
    } else if (String(memberCosts?.tier1 ?? "").includes("/")) {
        errors.tier1 = MEMBER_COSTS_VALIDATION_MESSAGES.TIER_SLASH;
    }

    if (String(memberCosts?.tier2 ?? "").includes("/")) {
        errors.tier2 = MEMBER_COSTS_VALIDATION_MESSAGES.TIER_SLASH;
    } else if (
        tier1 &&
        tier2 &&
        tier1.toLowerCase() === tier2.toLowerCase()
    ) {
        errors.tier2 = MEMBER_COSTS_VALIDATION_MESSAGES.TIER_NOT_UNIQUE;
    }

    if (memberCosts?.includeUnguidedColumn) {
        const unguidedRaw = String(memberCosts?.unguidedTierLabel ?? "");
        const unguidedTrimmed = unguidedRaw.trim();

        if (!unguidedTrimmed) {
            unguidedTierLabelError =
                MEMBER_COSTS_VALIDATION_MESSAGES.UNGUIDED_LABEL_REQUIRED;
            errors.unguidedTierLabel = unguidedTierLabelError;
        } else if (unguidedRaw.includes("/")) {
            unguidedTierLabelError =
                MEMBER_COSTS_VALIDATION_MESSAGES.UNGUIDED_LABEL_NO_SLASH;
            errors.unguidedTierLabel = unguidedTierLabelError;
        } else if (
            (tier1 && unguidedTrimmed.toLowerCase() === tier1.toLowerCase()) ||
            (tier2 && unguidedTrimmed.toLowerCase() === tier2.toLowerCase())
        ) {
            unguidedTierLabelError =
                MEMBER_COSTS_VALIDATION_MESSAGES.UNGUIDED_LABEL_NOT_UNIQUE;
            errors.unguidedTierLabel = unguidedTierLabelError;
        }
    }

    const tables = [
        { key: "medicalRows", rows: memberCosts?.medicalRows ?? [] },
        { key: "rxRows", rows: memberCosts?.rxRows ?? [] },
        { key: "rxSpecialtyRows", rows: memberCosts?.rxSpecialtyRows ?? [] },
    ];

    for (const { key, rows } of tables) {
        for (const row of rows) {
            if (row.isChecked && isBlank(row.category)) {
                errors[`${key}_${row.id}_category`] =
                    MEMBER_COSTS_VALIDATION_MESSAGES.CATEGORY_REQUIRED_IF_CHECKED;
            }
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
        unguidedTierLabelError,
    };
};

/**
 * @param {object} wireLists
 * @param {object} memberCosts
 * @returns {object}
 */
export const applyUnguidedOverlayToMemberCostWire = (wireLists, memberCosts) => {
    const tier1 = String(memberCosts.tier1 ?? "").trim();
    const tier2 = String(memberCosts.tier2 ?? "").trim();
    const includeUnguidedColumn = Boolean(memberCosts.includeUnguidedColumn);
    const trimmedUnguidedLabel = String(
        memberCosts.unguidedTierLabel ?? "",
    ).trim();
    const medicalRows = memberCosts.medicalRows ?? [];
    const shouldPublish = shouldPublishUnguidedAmounts(memberCosts);

    const unguidedAmountByCategory = new Map(
        medicalRows.map((row) => [row.category, row.unguidedInNetwork ?? ""]),
    );

    const fullMedicalCoverageList = (wireLists.fullMedicalCoverageList ?? []).map(
        (row) => {
            const formUnguided = unguidedAmountByCategory.get(row.category) ?? "";
            const trimmedFormUnguided = String(formUnguided).trim();
            const unguidedInNetwork =
                includeUnguidedColumn && trimmedFormUnguided.length > 0
                    ? trimmedFormUnguided
                    : "";

            return {
                ...row,
                unguidedInNetwork,
            };
        },
    );

    if (!includeUnguidedColumn) {
        return {
            ...wireLists,
            fullMedicalCoverageList,
            planTier: wireLists.planTier,
            hasUnguidedInNetwork: false,
            unguidedTierLabel: "",
        };
    }

    const unguidedTierLabelWire = trimmedUnguidedLabel;

    let individualMedicalCoverageList = (
        wireLists.individualMedicalCoverageList ?? []
    ).map((row) => {
        const nextRow = { ...row };
        delete nextRow.individualUnguided;
        return nextRow;
    });

    let familyMedicalCoverageList = (
        wireLists.familyMedicalCoverageList ?? []
    ).map((row) => {
        const nextRow = { ...row };
        delete nextRow.familyUnguided;
        return nextRow;
    });

    const stripUnguidedFromCoverageSummary = (coverageSummaryList) =>
        coverageSummaryList.map((group) => ({
            ...group,
            coverageItemList: group.coverageItemList.map((item) => {
                const nextItem = { ...item };
                if (trimmedUnguidedLabel) {
                    delete nextItem[trimmedUnguidedLabel];
                }
                return nextItem;
            }),
        }));

    let coverageSummaryList = stripUnguidedFromCoverageSummary(
        wireLists.coverageSummaryList ?? [],
    );

    if (!shouldPublish) {
        return {
            ...wireLists,
            fullMedicalCoverageList,
            individualMedicalCoverageList,
            familyMedicalCoverageList,
            coverageSummaryList,
            planTier: wireLists.planTier,
            hasUnguidedInNetwork: false,
            unguidedTierLabel: unguidedTierLabelWire,
        };
    }

    individualMedicalCoverageList = individualMedicalCoverageList.map((row) => {
        const formUnguided = unguidedAmountByCategory.get(row.category);
        const amount =
            formUnguided !== undefined
                ? formUnguided
                : row[trimmedUnguidedLabel] ?? "";
        return {
            ...row,
            individualUnguided: amount,
        };
    });

    familyMedicalCoverageList = familyMedicalCoverageList.map((row) => {
        const formUnguided = unguidedAmountByCategory.get(row.category);
        const amount =
            formUnguided !== undefined
                ? formUnguided
                : row[trimmedUnguidedLabel] ?? "";
        return {
            ...row,
            familyUnguided: amount,
        };
    });

    coverageSummaryList = coverageSummaryList.map((group) => ({
        ...group,
        coverageItemList: group.coverageItemList.map((item) => {
            const formUnguided = unguidedAmountByCategory.get(item.care);
            const amount =
                formUnguided !== undefined ? formUnguided : item[trimmedUnguidedLabel] ?? "";
            return {
                ...item,
                [trimmedUnguidedLabel]: amount,
            };
        }),
    }));

    let hasTier2Key = false;
    for (const group of coverageSummaryList) {
        for (const item of group.coverageItemList) {
            if (tier2 && Object.prototype.hasOwnProperty.call(item, tier2)) {
                hasTier2Key = true;
                break;
            }
        }
        if (hasTier2Key) {
            break;
        }
    }

    const planTierSegments = [tier1, trimmedUnguidedLabel];
    if (hasTier2Key && tier2) {
        planTierSegments.push(tier2);
    }
    const planTier = planTierSegments.filter(Boolean).join("/");

    return {
        ...wireLists,
        fullMedicalCoverageList,
        individualMedicalCoverageList,
        familyMedicalCoverageList,
        coverageSummaryList,
        planTier,
        hasUnguidedInNetwork: true,
        unguidedTierLabel: unguidedTierLabelWire,
    };
};

/**
 * @param {object} planData
 * @param {object} emptyMemberCosts
 * @returns {object}
 */
export const mapMemberCosts = (planData, emptyMemberCosts) => {
    const memberCosts = {
        ...emptyMemberCosts,
        medicalRows: [],
        rxRows: [],
        rxSpecialtyRows: [],
    };

    const headerFields = mapStoredPlanTierToMemberCostHeaders(planData);
    memberCosts.tier1 = headerFields.tier1;
    memberCosts.tier2 = headerFields.tier2;
    memberCosts.unguidedTierLabel = headerFields.unguidedTierLabel;
    memberCosts.includeUnguidedColumn = headerFields.includeUnguidedColumn;

    if (!Array.isArray(planData.fullMedicalCoverageList)) {
        return memberCosts;
    }

    const familyMedicalCoverageList = planData.familyMedicalCoverageList ?? [];
    const medicalRowsWithoutAccumulators =
        planData.fullMedicalCoverageList.filter(
            ({ category }) =>
                category !== "Deductible" && category !== "Out-of-pocket Max",
        );

    memberCosts.medicalRows = medicalRowsWithoutAccumulators.map(
        (row, index) => ({
            id: `mc-${index}`,
            category: row.category ?? "",
            inNetwork:
                row.inNetwork ||
                row.individualTier1 ||
                row.familyTier1 ||
                "",
            unguidedInNetwork: resolveRowUnguidedAmountFromStoredRow(row),
            outOfNetwork:
                row.outOfNetwork ||
                row.individualTier2 ||
                row.familyTier2 ||
                "",
            isChecked: isMemberCostCategoryChecked(
                familyMedicalCoverageList,
                row.category,
            ),
        }),
    );

    const rxCoverage = mapRxCoverageTable(planData.rxCoverageList);
    memberCosts.rxHeader1 = rxCoverage.rxHeader1;
    memberCosts.rxHeader2 = rxCoverage.rxHeader2;
    memberCosts.rxRows = rxCoverage.rxRows;

    const rxSpecialtyCoverage = mapRxCoverageTable(
        planData.rxCoverageSpecialtyList,
    );
    memberCosts.rxSpecialtyHeader1 = rxSpecialtyCoverage.rxHeader1;
    memberCosts.rxSpecialtyHeader2 = rxSpecialtyCoverage.rxHeader2;
    memberCosts.rxSpecialtyRows = rxSpecialtyCoverage.rxRows;

    return memberCosts;
};

/**
 * @param {object} planData
 * @param {object} emptyAccumulators
 * @param {boolean} isHrp
 * @returns {object}
 */
export const mapAccumulators = (planData, emptyAccumulators, isHrp) => {
    if (isHrp) {
        return { ...emptyAccumulators };
    }

    const accumsConfig = planData.accumsConfig ?? {};
    const accumulators = { ...emptyAccumulators };

    for (const [key, value] of Object.entries(accumsConfig)) {
        if (key.endsWith("Disabled")) {
            const enabledKey = key.replace("Disabled", "Enabled");
            accumulators[enabledKey] = !value;
            continue;
        }

        if (key.endsWith("Enabled")) {
            accumulators[key] = value;
            continue;
        }

        if (key === "inNetworkLabel" || key === "outOfNetworkLabel") {
            accumulators[key] = normalizeAccumulatorNetworkLabel(key, value);
            continue;
        }

        accumulators[key] =
            value === null || value === undefined ? "" : String(value);
    }

    return accumulators;
};

/**
 * @param {string | undefined} documentUrl
 * @returns {string}
 */
const extractDocumentFileName = (documentUrl) => {
    if (!documentUrl) {
        return "";
    }

    const segments = documentUrl.split("/");
    return segments[segments.length - 1] ?? "";
};

/**
 * @param {object} planData
 * @param {object} emptySetup
 * @returns {object}
 */
export const mapSetupDocuments = (planData, emptySetup) => ({
    ...emptySetup,
    spdFileName: extractDocumentFileName(planData.spdUrl),
    sbcFileName: extractDocumentFileName(planData.sbcUrl),
    spdUrl: typeof planData.spdUrl === "string" ? planData.spdUrl : "",
    sbcUrl: typeof planData.sbcUrl === "string" ? planData.sbcUrl : "",
});

/**
 * Maps a medical plan response body into the Plan Setup wizard form shape.
 * @param {object | null | undefined} planData
 * @param {boolean} [isHrp=false]
 * @returns {object}
 */
export const mapMedicalPlanToSetupForm = (planData, isHrp = false) => {
    const emptyForm = createEmptyPlanSetupForm();

    if (!planData) {
        return emptyForm;
    }

    return {
        planDesign: mapPlanDesign(planData),
        vendorDetails: mapVendorDetails(planData),
        memberCosts: mapMemberCosts(planData, emptyForm.memberCosts),
        accumulators: mapAccumulators(
            planData,
            emptyForm.accumulators,
            isHrp,
        ),
        setup: mapSetupDocuments(planData, emptyForm.setup),
    };
};

/**
 * Forbidden key paths on every medical-plan v2 write body.
 * @readonly
 */
export const MEDICAL_PLAN_V2_FORBIDDEN_KEY_PATHS = Object.freeze([
    "uuid",
    "networkAffiliation",
    "networkAffiliation.uuid",
]);

/**
 * Always-emitted Locator + wizard Keep keys on the medical-plan v2 write body.
 * @readonly
 */
export const MEDICAL_PLAN_V2_REQUIRED_KEY_PATHS = Object.freeze([
    "clientId",
    "planId",
    "planName",
    "planStartDate",
    "planEndDate",
    "cmsId",
    "networkName",
    "phoneNumberMemberServices",
    "supportHours",
    "senderEmail",
    "planType",
    "fullDirectory",
    "portalUrl",
    "mfa",
    "passwordHelperText",
    "mfaPasswordRequirements",
    "pcpRequired",
    "virtualPrimaryCareEnabled",
    "referralRequired",
    "referralAuthType",
    "telemedicine",
    "pbmName",
    "pbmLink",
    "formularyUrl",
    "fullMedicalCoverageList",
    "planTier",
    "rxCoverageList",
    "rxCoverageSpecialtyList",
    "coverageSummaryList",
    "individualMedicalCoverageList",
    "familyMedicalCoverageList",
    "hasUnguidedInNetwork",
    "unguidedTierLabel",
    "idCardLogoUrl",
    "spdUrl",
    "sbcUrl",
]);

const MFA_PASSWORD_REQUIREMENT_KEYS = Object.freeze([
    "lettersNo",
    "numbersNo",
    "specialCharactersNo",
    "minCharactersNo",
]);

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
const parsePlanSetupDateForWire = (value) => {
    if (value == null || value === "") {
        return "";
    }

    const locale = "en-US";
    const nyTimeZone = "America/New_York";
    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return "";
    }

    const localeParsedDate = new Date(
        parsedDate.toLocaleString(locale, { timeZone: nyTimeZone }),
    );

    if (parsedDate.getDate() !== localeParsedDate.getDate()) {
        if (parsedDate.getDate() > localeParsedDate.getDate()) {
            parsedDate.setDate(parsedDate.getDate() + 1);
        } else {
            parsedDate.setDate(parsedDate.getDate() - 1);
        }
    }

    return parsedDate.toLocaleString(locale, { timeZone: nyTimeZone });
};

/**
 * @param {string} key
 * @param {number} value
 * @returns {string}
 */
const buildMfaPasswordRequirementSegment = (key, value) => {
    if (value === 0) {
        return "";
    }

    switch (key) {
        case "lettersNo":
            return value === 1
                ? "(?=.*[a-z])(?=.*[A-Z])"
                : `(?=.*[a-z]{${value}})(?=.*[A-Z]{${value}})`;
        case "numbersNo":
            return value === 1 ? "(?=.*\\d)" : `(?=.*\\d{${value}})`;
        case "specialCharactersNo":
            return value === 1
                ? "(?=.*[@$!%*#?&])"
                : `(?=.*[@$!%*#?&]{${value}})`;
        case "minCharactersNo":
            return `[A-Za-z\\d@$!%*#?&]{${value},}`;
        default:
            return "";
    }
};

/**
 * @param {object} planDesign
 * @returns {string}
 */
const buildMfaPasswordRequirements = (planDesign = {}) => {
    const segments = [];

    for (const key of MFA_PASSWORD_REQUIREMENT_KEYS) {
        const rawValue = planDesign[key];
        if (rawValue === "" || rawValue == null) {
            continue;
        }

        const numericValue = Number(rawValue);
        if (Number.isNaN(numericValue) || numericValue === 0) {
            continue;
        }

        const segment = buildMfaPasswordRequirementSegment(key, numericValue);
        if (segment) {
            segments.push(segment);
        }
    }

    return `^${segments.join("")}$`;
};

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
const ensureHttpsUrl = (value) => {
    if (value == null || value === "") {
        return "";
    }

    const url = String(value);
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }

    return `https://${url}`;
};

/**
 * @param {object} accumulators
 * @returns {object}
 */
const buildAccumsConfigForWire = (accumulators = {}) => {
    const accumsConfig = {};

    for (const [key, value] of Object.entries(accumulators)) {
        if (key.endsWith("Enabled")) {
            const disabledKey = key.replace(/Enabled$/, "Disabled");
            accumsConfig[disabledKey] = !value;
            continue;
        }

        if (key === "inNetworkLabel" || key === "outOfNetworkLabel") {
            accumsConfig[key] = String(value ?? "").toUpperCase();
            continue;
        }

        accumsConfig[key] =
            value === null || value === undefined ? "" : String(value);
    }

    return accumsConfig;
};

/**
 * @param {boolean} disabled
 * @param {string | number | undefined} amount
 * @returns {boolean}
 */
const isAccumulatorAmountDisabledOrNone = (disabled, amount) =>
    Boolean(disabled) ||
    amount === undefined ||
    amount === "" ||
    amount === 0;

/**
 * @param {boolean} disabled
 * @param {string | number | undefined} amount
 * @returns {string}
 */
const formatAccumulatorMoneyOrDash = (disabled, amount) => {
    if (isAccumulatorAmountDisabledOrNone(disabled, amount)) {
        return "-";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

/**
 * @param {object} accumsConfig
 * @param {string} tier1
 * @param {string} tier2
 * @returns {{ familyCoverageItemList: object[], individualCoverageItemList: object[] }}
 */
const buildAccumulatorCoverageSeedLists = (accumsConfig, tier1, tier2) => {
    const familyCoverageItemList = [];
    const individualCoverageItemList = [];

    const familyDeductible = {
        care: "Deductible",
        [tier1]: formatAccumulatorMoneyOrDash(
            accumsConfig.inNetworkDeductibleFamilyAmountDisabled,
            accumsConfig.inNetworkDeductibleFamilyAmount,
        ),
        ...(!accumsConfig.outOfNetworkDeductibleFamilyAmountDisabled &&
            tier2 && {
                [tier2]: formatAccumulatorMoneyOrDash(
                    false,
                    accumsConfig.outOfNetworkDeductibleFamilyAmount,
                ),
            }),
    };
    familyCoverageItemList.push(familyDeductible);

    const familyOutOfPocketMax = {
        care: "Out-of-pocket Max",
        [tier1]: formatAccumulatorMoneyOrDash(
            accumsConfig.inNetworkOutOfPocketFamilyMaxAmountDisabled,
            accumsConfig.inNetworkOutOfPocketFamilyMaxAmount,
        ),
        ...(!accumsConfig.outOfNetworkOutOfPocketFamilyMaxAmountDisabled &&
            tier2 && {
                [tier2]: formatAccumulatorMoneyOrDash(
                    false,
                    accumsConfig.outOfNetworkOutOfPocketFamilyMaxAmount,
                ),
            }),
    };
    familyCoverageItemList.push(familyOutOfPocketMax);

    const individualDeductible = {
        care: "Deductible",
        [tier1]: formatAccumulatorMoneyOrDash(
            accumsConfig.inNetworkDeductiblePatientAmountDisabled,
            accumsConfig.inNetworkDeductiblePatientAmount,
        ),
        ...(!accumsConfig.outOfNetworkDeductiblePatientAmountDisabled &&
            tier2 && {
                [tier2]: formatAccumulatorMoneyOrDash(
                    false,
                    accumsConfig.outOfNetworkDeductiblePatientAmount,
                ),
            }),
    };
    individualCoverageItemList.push(individualDeductible);

    const individualOutOfPocketMax = {
        care: "Out-of-pocket Max",
        [tier1]: formatAccumulatorMoneyOrDash(
            accumsConfig.inNetworkOutOfPocketPatientMaxAmountDisabled,
            accumsConfig.inNetworkOutOfPocketPatientMaxAmount,
        ),
        ...(!accumsConfig.outOfNetworkOutOfPocketPatientMaxAmountDisabled &&
            tier2 && {
                [tier2]: formatAccumulatorMoneyOrDash(
                    false,
                    accumsConfig.outOfNetworkOutOfPocketPatientMaxAmount,
                ),
            }),
    };
    individualCoverageItemList.push(individualOutOfPocketMax);

    return { familyCoverageItemList, individualCoverageItemList };
};

/**
 * @param {object[]} rows
 * @param {string} header1
 * @param {string} header2
 * @returns {object[]}
 */
const buildRxCoverageWireList = (rows = [], header1 = "", header2 = "") => {
    const list = [{ row: ["", header1, header2] }];

    for (const row of rows) {
        list.push({
            row: [
                row.category,
                row.userInput1,
                row.userInput2,
                row.isChecked,
            ],
        });
    }

    return list;
};

/**
 * @param {{ memberCosts: object, accumulators: object, isHrp: boolean }} params
 * @returns {object}
 */
const buildMemberCostWireLists = ({
    memberCosts = {},
    accumulators = {},
    isHrp = false,
}) => {
    const tier1 = String(memberCosts.tier1 ?? "").trim();
    const tier2 = String(memberCosts.tier2 ?? "").trim();
    const medicalRows = Array.isArray(memberCosts.medicalRows)
        ? memberCosts.medicalRows
        : [];

    const fullMedicalCoverageList = medicalRows.map((row) => ({
        category: row.category,
        inNetwork: row.inNetwork,
        outOfNetwork: row.outOfNetwork,
        isChecked: row.isChecked,
    }));

    const accumsConfig = isHrp
        ? null
        : buildAccumsConfigForWire(accumulators);
    const { familyCoverageItemList, individualCoverageItemList } = isHrp
        ? { familyCoverageItemList: [], individualCoverageItemList: [] }
        : buildAccumulatorCoverageSeedLists(accumsConfig, tier1, tier2);

    let familyMedicalCoverageList = familyCoverageItemList.map((item) => {
        const mapped = {
            category: item.care,
            familyTier1: item[tier1],
        };
        if (tier2 && Object.prototype.hasOwnProperty.call(item, tier2)) {
            mapped.familyTier2 = item[tier2];
        }
        return mapped;
    });

    let individualMedicalCoverageList = individualCoverageItemList.map(
        (item) => {
            const mapped = {
                category: item.care,
                individualTier1: item[tier1],
            };
            if (tier2 && Object.prototype.hasOwnProperty.call(item, tier2)) {
                mapped.individualTier2 = item[tier2];
            }
            return mapped;
        },
    );

    const checkedRowsWithAmounts = medicalRows.filter(
        (row) =>
            row.isChecked &&
            (row.inNetwork ||
                row.outOfNetwork ||
                String(row.unguidedInNetwork ?? "").trim()),
    );

    familyMedicalCoverageList = familyMedicalCoverageList.concat(
        checkedRowsWithAmounts.map((row) => ({
            category: row.category,
            familyTier1: row.inNetwork,
            familyTier2: row.outOfNetwork,
        })),
    );

    individualMedicalCoverageList = individualMedicalCoverageList.concat(
        checkedRowsWithAmounts.map((row) => ({
            category: row.category,
            individualTier1: row.inNetwork,
            individualTier2: row.outOfNetwork,
        })),
    );

    const familyCoverageListItem = {
        coverageType: "Family",
        coverageItemList: [],
    };
    const individualCoverageListItem = {
        coverageType: "Individual",
        coverageItemList: [],
    };

    for (const row of fullMedicalCoverageList) {
        if (!row.isChecked) {
            continue;
        }

        const outOfNetwork = row.outOfNetwork;
        const hasOutOfNetworkMemberCost = outOfNetwork
            ? String(outOfNetwork).trim().length > 0
            : false;

        const familyObj = {
            care: row.category,
            [tier1]: row.inNetwork,
        };
        if (hasOutOfNetworkMemberCost && tier2) {
            familyObj[tier2] = row.outOfNetwork;
        }
        familyCoverageListItem.coverageItemList.push(familyObj);

        const individualObj = {
            care: row.category,
            [tier1]: row.inNetwork,
        };
        if (hasOutOfNetworkMemberCost && tier2) {
            individualObj[tier2] = row.outOfNetwork;
        }
        individualCoverageListItem.coverageItemList.push(individualObj);
    }

    const coverageSummaryList = [
        familyCoverageListItem,
        individualCoverageListItem,
    ];

    let hasTier2 = false;
    for (const item of familyCoverageListItem.coverageItemList) {
        if (tier2 && Object.prototype.hasOwnProperty.call(item, tier2)) {
            hasTier2 = true;
            break;
        }
    }
    if (!hasTier2) {
        for (const item of individualCoverageListItem.coverageItemList) {
            if (tier2 && Object.prototype.hasOwnProperty.call(item, tier2)) {
                hasTier2 = true;
                break;
            }
        }
    }

    const planTier =
        hasTier2 && tier2 ? `${tier1}/${tier2}` : tier1;

    const baseWire = {
        fullMedicalCoverageList,
        individualMedicalCoverageList,
        familyMedicalCoverageList,
        coverageSummaryList,
        planTier,
        rxCoverageList: buildRxCoverageWireList(
            memberCosts.rxRows,
            memberCosts.rxHeader1 ?? "",
            memberCosts.rxHeader2 ?? "",
        ),
        rxCoverageSpecialtyList: buildRxCoverageWireList(
            memberCosts.rxSpecialtyRows,
            memberCosts.rxSpecialtyHeader1 ?? "",
            memberCosts.rxSpecialtyHeader2 ?? "",
        ),
        ...(isHrp ? {} : { accumsConfig }),
    };

    return applyUnguidedOverlayToMemberCostWire(baseWire, memberCosts);
};

/**
 * Builds a form-shaped medical plan v2 update body from Plan Setup wizard state.
 * Starts from an empty object (does not clone a loaded GET plan).
 * @param {{ form: object, clientId: string, planId: string, isHrp: boolean }} params
 * @returns {object} Form-shaped v2 wire body
 */
export const buildMedicalPlanV2UpdateBody = ({
    form = {},
    clientId,
    planId,
    isHrp = false,
}) => {
    const planDesign = form.planDesign ?? {};
    const vendorDetails = form.vendorDetails ?? {};
    const memberCosts = form.memberCosts ?? {};
    const accumulators = form.accumulators ?? {};
    const setup = form.setup ?? {};

    const memberCostWire = buildMemberCostWireLists({
        memberCosts,
        accumulators,
        isHrp,
    });

    const body = {
        clientId,
        planId,
        planName: planDesign.planName ?? "",
        planStartDate: parsePlanSetupDateForWire(planDesign.startDate),
        planEndDate: parsePlanSetupDateForWire(planDesign.endDate),
        planType: planDesign.planType ?? "",
        fullDirectory: Boolean(planDesign.fullDirectory),
        cmsId: planDesign.cmsId ?? "",
        networkName: planDesign.cmsName ?? "",
        phoneNumberMemberServices: planDesign.contactNumber ?? "",
        supportHours: planDesign.supportHours ?? "",
        senderEmail: planDesign.senderEmail ?? "",
        portalUrl: planDesign.portalUrl ?? "",
        pcpRequired: planDesign.pcpDesignation === "required",
        virtualPrimaryCareEnabled: Boolean(planDesign.vpcEnabled),
        referralRequired: planDesign.referrals ?? "no",
        referralAuthType: planDesign.referralType ?? "",
        mfa: planDesign.mfa ?? "",
        passwordHelperText: planDesign.passwordHelperText ?? "",
        mfaPasswordRequirements: buildMfaPasswordRequirements(planDesign),
        telemedicine: vendorDetails.telemedicine ?? "",
        pbmName:
            vendorDetails.pbmName === "Other" ||
            vendorDetails.pbmName == null
                ? null
                : vendorDetails.pbmName,
        pbmLink: ensureHttpsUrl(vendorDetails.pbmSiteURL),
        formularyUrl: ensureHttpsUrl(vendorDetails.formularyUrl),
        idCardLogoUrl:
            typeof setup.idCardLogoUrl === "string" ? setup.idCardLogoUrl : "",
        spdUrl: typeof setup.spdUrl === "string" ? setup.spdUrl : "",
        sbcUrl: typeof setup.sbcUrl === "string" ? setup.sbcUrl : "",
        ...memberCostWire,
    };

    return body;
};

/**
 * Standard error messages for plan configuration import.
 * @readonly
 */
export const PLAN_IMPORT_ERROR_MESSAGES = Object.freeze({
    INVALID_EXTENSION:
        "Invalid file type: Please select a .json plan configuration file.",
    INVALID_JSON:
        "Invalid file content: The selected file could not be parsed as valid JSON.",
    CANNOT_IMPORT: "Cannot import plan",
    MISMATCH: (fields, isHrp = false) => {
        const hasClientIdMismatch = fields.includes("Client ID");
        const hasPlanIdMismatch = fields.includes("Plan ID");

        if (hasClientIdMismatch && hasPlanIdMismatch) {
            return "Cannot import plan: Both Client ID and Plan ID do not match the target plan. Please verify that you selected the correct file.";
        }
        if (hasClientIdMismatch) {
            return "Cannot import plan: Client ID does not match the target client. Plans cannot be imported across different clients.";
        }
        if (hasPlanIdMismatch) {
            return isHrp
                ? "Cannot import plan: Plan ID does not match the target plan."
                : "Cannot import plan: Plan ID does not match the target plan. On Javelina, imported configurations must match the target Plan ID.";
        }
        return "Cannot import plan: The imported configuration does not match the target plan.";
    },
});

/**
 * Validates that an import file has a .json extension.
 * @param {string | null | undefined} fileName
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validatePlanImportFileName = (fileName) => {
    const name = String(fileName ?? "");
    const extension = name.substring(name.lastIndexOf(".") + 1).toLowerCase();
    const isValid = extension === "json";
    return {
        isValid,
        error: isValid ? undefined : PLAN_IMPORT_ERROR_MESSAGES.INVALID_EXTENSION,
    };
};

/**
 * Safely parses raw text as JSON for plan import.
 * @param {string} jsonText
 * @returns {{ isValid: boolean, data?: object, error?: string }}
 */
export const parsePlanImportJson = (jsonText) => {
    if (typeof jsonText !== "string" || jsonText.trim() === "") {
        return {
            isValid: false,
            error: PLAN_IMPORT_ERROR_MESSAGES.INVALID_JSON,
        };
    }
    try {
        const data = JSON.parse(jsonText);
        if (data == null || typeof data !== "object") {
            return {
                isValid: false,
                error: PLAN_IMPORT_ERROR_MESSAGES.INVALID_JSON,
            };
        }
        return {
            isValid: true,
            data,
        };
    } catch {
        return {
            isValid: false,
            error: PLAN_IMPORT_ERROR_MESSAGES.INVALID_JSON,
        };
    }
};

const removePlanIdPrefix = (str) => String(str ?? "").replace(/^HRP_/, "");

/**
 * Validates that imported plan metadata matches target row identifiers.
 * @param {{ fileData: object, targetRow: object, isHrp?: boolean }} params
 * @returns {{
 *   canImport: boolean,
 *   requiresConfirmation: boolean,
 *   mismatchFields: Array<string>,
 *   importedPlanId?: string,
 *   targetPlanId?: string,
 *   error?: string
 * }}
 */
export const validatePlanImportIds = ({
    fileData,
    targetRow,
    isHrp = false,
}) => {
    const importedPlanId = removePlanIdPrefix(fileData?.planId);
    const currentPlanId = removePlanIdPrefix(targetRow?.planId);
    const isMatchPlanId = importedPlanId === currentPlanId;
    const isSameClientId =
        String(fileData?.clientId ?? "") === String(targetRow?.clientId ?? "");

    const mismatchFields = [];
    if (!isMatchPlanId) mismatchFields.push("Plan ID");
    if (!isSameClientId) mismatchFields.push("Client ID");

    if (mismatchFields.length > 0) {
        if (!isHrp || !isSameClientId) {
            return {
                canImport: false,
                requiresConfirmation: false,
                mismatchFields,
                error: PLAN_IMPORT_ERROR_MESSAGES.MISMATCH(
                    mismatchFields,
                    isHrp,
                ),
            };
        }

        return {
            canImport: true,
            requiresConfirmation: true,
            mismatchFields,
            importedPlanId: fileData?.planId ?? "",
            targetPlanId: targetRow?.planId ?? "",
        };
    }

    return {
        canImport: true,
        requiresConfirmation: false,
        mismatchFields: [],
    };
};

/**
 * Strips network affiliations prior to exporting plan JSON blob.
 * @param {object} payload
 * @returns {object}
 */
export const sanitizePlanExportPayload = (payload) => {
    if (payload == null || typeof payload !== "object") {
        return payload;
    }
    const cloned = deepCloneJson(payload);
    delete cloned.networkAffiliation;
    delete cloned.networkAffilication;
    return cloned;
};

/**
 * Builds the canonical filename for plan configuration JSON exports.
 * @param {{ clientName?: string, planId?: string, date?: Date }} params
 * @returns {string}
 */
export const buildPlanExportFileName = ({
    clientName = "",
    planId = "",
    date = new Date(),
}) => {
    const parsedDate =
        date instanceof Date && !Number.isNaN(date.getTime())
            ? date
            : new Date();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();
    const formattedDate = `${month}-${day}-${year}`;

    const safeClientName = String(clientName ?? "").trim();
    const safePlanId = String(planId ?? "").trim();

    return `${safeClientName}_${safePlanId}_${formattedDate}.json`;
};

/**
 * Constructs a drill-down path with URL query parameters for plan navigation.
 * @param {string} basePath - Target route (e.g. "/plan-manager/plans/setup").
 * @param {{ clientId?: string, planId?: string, platformTab?: string }} [params={}] - Target plan identity.
 * @returns {string} - Formatted URL with encoded query string.
 */
export const buildPlanDrillDownUrl = (basePath, params = {}) => {
    if (!basePath) {
        return "";
    }

    const clientId = String(params.clientId ?? "").trim();
    const planId = String(params.planId ?? "").trim();
    const platform = String(params.platformTab ?? "").trim();

    if (!clientId && !planId && !platform) {
        return basePath;
    }

    const searchParams = new URLSearchParams();
    if (clientId) {
        searchParams.set("clientId", clientId);
    }
    if (planId) {
        searchParams.set("planId", planId);
    }
    if (platform) {
        searchParams.set("platform", platform);
    }

    const queryString = searchParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Parses and sanitizes plan query parameters from URLSearchParams or query string.
 * @param {URLSearchParams|string|null|undefined} search - URL search params instance or query string.
 * @returns {{ clientId: string|null, planId: string|null, platformTab: string|null }}
 */
export const parsePlanRouteSearchParams = (search) => {
    if (!search) {
        return { clientId: null, planId: null, platformTab: null };
    }

    const searchParams =
        search instanceof URLSearchParams
            ? search
            : new URLSearchParams(typeof search === "string" ? search : "");

    const rawClientId = searchParams.get("clientId");
    const rawPlanId = searchParams.get("planId");
    const rawPlatform = searchParams.get("platform");

    const clientId =
        rawClientId && rawClientId.trim() !== "" ? rawClientId.trim() : null;
    const planId =
        rawPlanId && rawPlanId.trim() !== "" ? rawPlanId.trim() : null;
    const platform =
        rawPlatform && rawPlatform.trim() !== ""
            ? rawPlatform.trim().toUpperCase()
            : null;

    const platformTab =
        platform === "HRP" || platform === "JAVELINA" ? platform : null;

    return {
        clientId,
        planId,
        platformTab,
    };
};

/**
 * Finds a plan row from Javelina and HRP plan grid datasets by clientId and planId.
 * @param {{ jvPlans?: Array<object>, hrpPlans?: Array<object>, clientId?: string, planId?: string, platformTab?: string }} [params={}]
 * @returns {object|null} Matching plan grid row or null if not found.
 */
export const findPlanInGrids = ({
    jvPlans = [],
    hrpPlans = [],
    clientId,
    planId,
    platformTab,
} = {}) => {
    if (!clientId || !planId) {
        return null;
    }

    const targetClientId = String(clientId).trim();
    const targetPlanId = String(planId).trim();

    if (!targetClientId || !targetPlanId) {
        return null;
    }

    const matchesPlan = (row) =>
        String(row?.clientId ?? "").trim() === targetClientId &&
        String(row?.planId ?? "").trim() === targetPlanId;

    if (platformTab === "HRP") {
        return (Array.isArray(hrpPlans) ? hrpPlans : []).find(matchesPlan) ?? null;
    }

    if (platformTab === "JAVELINA") {
        return (Array.isArray(jvPlans) ? jvPlans : []).find(matchesPlan) ?? null;
    }

    if (targetPlanId.startsWith("HRP_")) {
        const hrpMatch = (Array.isArray(hrpPlans) ? hrpPlans : []).find(matchesPlan);
        if (hrpMatch) return hrpMatch;
        return (Array.isArray(jvPlans) ? jvPlans : []).find(matchesPlan) ?? null;
    }

    const jvMatch = (Array.isArray(jvPlans) ? jvPlans : []).find(matchesPlan);
    if (jvMatch) return jvMatch;
    return (Array.isArray(hrpPlans) ? hrpPlans : []).find(matchesPlan) ?? null;
};

/**
 * Determines whether all, some, or none of the rows in a cost table are checked.
 * @param {Array<{ isChecked?: boolean }>} [rows=[]]
 * @returns {{ isAllChecked: boolean, isIndeterminate: boolean, hasRows: boolean }}
 */
export const getCostTableSelectionState = (rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { isAllChecked: false, isIndeterminate: false, hasRows: false };
    }
    const checkedCount = rows.filter((row) => Boolean(row?.isChecked)).length;
    const isAllChecked = checkedCount === rows.length;
    const isIndeterminate = checkedCount > 0 && checkedCount < rows.length;
    return { isAllChecked, isIndeterminate, hasRows: true };
};

/**
 * Returns a new array of rows with isChecked set across all items.
 * If targetChecked is omitted, inverts the all-checked state (unchecks all if all were checked; checks all otherwise).
 * @param {Array<object>} [rows=[]]
 * @param {boolean} [targetChecked] - Optional explicit boolean override.
 * @returns {Array<object>} New array of rows with updated isChecked properties.
 */
export const toggleAllCostTableRows = (rows = [], targetChecked) => {
    if (!Array.isArray(rows)) {
        return [];
    }
    const shouldCheck =
        typeof targetChecked === "boolean"
            ? targetChecked
            : !rows.every((row) => Boolean(row?.isChecked));

    return rows.map((row) => ({
        ...row,
        isChecked: shouldCheck,
    }));
};




